import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Heart, Smile, Frown, Activity, Flame, MessageCircle, BookOpen, Target,
  LogOut, Send, Trash2, Plus, Check, Sparkles, User as UserIcon, LogIn, UserPlus,
  Globe, BarChart3, RefreshCw, Download, Brain, Search, Image as ImageIcon, FileText, Upload, Wand2, X,
  Shield, Lock, Cpu, Layers, Award, Zap, Server, Eye, Languages, Users, TrendingUp, LineChart, HeartPulse, Dumbbell, Salad, AlertCircle, Calendar, Bell, MapPin, Clock, Star, MessageSquare, Mail, Paperclip, Camera, Mic, MicOff, Volume2, VolumeX, Square, ChevronDown
} from "lucide-react";
import { LANGUAGES, isRTL, t as translate } from "./i18n";
import { PrivacyPolicy, TermsOfService, DataDeletion } from "./Legal";
import { IMG_SUGGESTIONS, FEATURED_SUGGESTIONS } from "./imageSuggestions";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Convert URLs in text into clickable links that open in a new tab.
function linkify(text) {
  if (!text || typeof text !== "string") return text;
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    let url = match[0];
    // Strip trailing punctuation that's commonly not part of the URL
    let trailing = "";
    while (url && /[.,)!?؟،;:]$/.test(url)) {
      trailing = url.slice(-1) + trailing;
      url = url.slice(0, -1);
    }
    parts.push(
      <a key={`url-${key++}-${match.index}`}
         href={url}
         target="_blank"
         rel="noopener noreferrer"
         className="underline font-semibold break-all"
         style={{ color: "var(--primary)" }}
         data-testid="chat-link">
        {url}
      </a>
    );
    if (trailing) parts.push(trailing);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// ---------- Auth Context ----------
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

// Convenience hook for translations
const useT = () => {
  const { lang } = useAuth();
  return (key) => translate(lang, key);
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [token, setToken] = useState(localStorage.getItem("hayatak_token") || null);
  const [lang, setLangState] = useState(localStorage.getItem("hayatak_lang") || "ar");

  useEffect(() => {
    // apply dir
    const rtl = isRTL(lang);
    document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  useEffect(() => {
    const check = async () => {
      // If returning from OAuth callback, let AuthCallback handle it first.
      if (typeof window !== "undefined" && (
        window.location.pathname.startsWith("/auth/callback") ||
        (window.location.hash || "").includes("session_id=") ||
        (window.location.hash || "").includes("token=")
      )) {
        return;
      }
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${API}/auth/me`, { headers });
        setUser(data.user);
        if (data.user?.language) {
          setLangState(data.user.language);
          localStorage.setItem("hayatak_lang", data.user.language);
        }
      } catch {
        setUser(false);
      }
    };
    check();
  }, [token]);

  const saveAuth = (u, t) => {
    setUser(u);
    setToken(t);
    if (t) localStorage.setItem("hayatak_token", t);
    if (u?.language) {
      setLangState(u.language);
      localStorage.setItem("hayatak_lang", u.language);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem("hayatak_token");
    setToken(null);
    setUser(false);
  };

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const setLang = async (newLang) => {
    setLangState(newLang);
    localStorage.setItem("hayatak_lang", newLang);
    if (user) {
      try {
        const { data } = await axios.put(`${API}/profile`, { language: newLang }, { headers: authHeaders });
        if (data.user) setUser(data.user);
      } catch {}
    }
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, token, saveAuth, logout, authHeaders, lang, setLang }}>
      {children}
    </AuthCtx.Provider>
  );
}

// ---------- Theme (age + mood) ----------
const MOOD_PALETTES = {
  happy:     { primary: "#FBBF24", accent: "#FFDAB9", overlay: "linear-gradient(135deg, rgba(253,230,138,0.35), rgba(255,218,185,0.25))" },
  sad:       { primary: "#B39DDB", accent: "#FFE4E1", overlay: "linear-gradient(135deg, rgba(230,230,250,0.45), rgba(255,228,225,0.3))" },
  stressed:  { primary: "#8FBC8F", accent: "#BFDBFE", overlay: "linear-gradient(135deg, rgba(189,218,203,0.4), rgba(191,219,254,0.3))" },
  motivated: { primary: "#FF7F50", accent: "#FFA07A", overlay: "linear-gradient(135deg, rgba(255,127,80,0.3), rgba(255,160,122,0.25))" },
  neutral:   { primary: "#D4A373", accent: "#FAEDCD", overlay: "linear-gradient(135deg, rgba(250,237,205,0.3), rgba(253,251,247,0.2))" },
};

const ageGroupOf = (age) => {
  const a = Number(age) || 25;
  if (a < 20) return "teen";
  if (a < 35) return "young";
  if (a < 55) return "adult";
  return "senior";
};

function ThemeManager({ age, mood }) {
  useEffect(() => {
    const group = ageGroupOf(age);
    document.documentElement.setAttribute("data-age-group", group);
    // Lock to dark golden theme; mood subtly tints the accent only.
    // Keep --primary stable across moods so buttons stay readable.
    document.documentElement.style.setProperty("--primary", "#E8C547");
    document.documentElement.style.setProperty("--primary-fg", "#2a1f17");
    document.documentElement.style.setProperty("--accent", "#E8C547");
    document.documentElement.style.setProperty("--overlay", "none");
    document.documentElement.style.setProperty("--ring", "#E8C54755");
  }, [age, mood]);
  return null;
}

// ---------- Language Switcher ----------
function LanguageSwitcher({ compact = false }) {
  const { lang, setLang } = useAuth();
  return (
    <div className="relative inline-flex items-center" data-testid="language-switcher">
      <Globe size={14} className="absolute pointer-events-none" style={{
        insetInlineStart: "0.6rem",
        color: "rgba(232, 197, 71, 0.8)"
      }} />
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        className="rounded-full ps-7 pe-3 py-1.5 text-sm font-semibold cursor-pointer"
        data-testid="language-select"
        style={{
          paddingInlineStart: "1.8rem",
          background: "rgba(26, 20, 16, 0.65)",
          color: "#FAF6F0",
          border: "1px solid rgba(232, 197, 71, 0.35)",
        }}
      >
        {LANGUAGES.map(l => (
          <option key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
            {l.name}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------- Install (PWA) Button ----------
function InstallButton() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const { lang } = useAuth();

  useEffect(() => {
    const onBefore = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    // Already running standalone?
    if (window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true) {
      setInstalled(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !prompt) return null;

  const label = lang === "ar" ? "ثبّت التطبيق" : "Install app";

  return (
    <button
      onClick={async () => {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === "accepted") setPrompt(null);
      }}
      className="btn"
      data-testid="install-pwa-btn"
      title={label}
    >
      <Download size={16}/> {label}
    </button>
  );
}

// ---------- Mood Helpers ----------
const MOODS = [
  { key: "happy",     icon: Smile,    color: "#FBBF24" },
  { key: "neutral",   icon: Heart,    color: "#D4A373" },
  { key: "sad",       icon: Frown,    color: "#B39DDB" },
  { key: "stressed",  icon: Activity, color: "#8FBC8F" },
  { key: "motivated", icon: Flame,    color: "#FF7F50" },
];
const moodLabel = (lang, key) => translate(lang, `mood_${key}`);

// ---------- Layout ----------
function Shell({ children, mood, onMoodChange }) {
  const { user, logout } = useAuth();
  const t = useT();
  const [showFeedback, setShowFeedback] = useState(false);
  return (
    <div className="grain min-h-screen">
      <ThemeManager age={user?.age} mood={mood} />
      <div className="mood-bg" />
      <header className="glass sticky top-0 z-20" data-testid="app-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles size={22} style={{ color: "var(--primary)" }} />
            <h1 className="text-xl sm:text-2xl font-extrabold">{t("app_name")}</h1>
            <span className="text-xs text-stone-500 hidden sm:inline">· {t("your_companion")} {user?.companion_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <InstallButton />
            <div className="hidden sm:flex items-center gap-1 bg-white/60 px-3 py-1.5 rounded-full border border-stone-200">
              <UserIcon size={14} />
              <span className="text-sm font-semibold">{user?.name}</span>
            </div>
            <button className="btn-ghost btn" onClick={() => setShowFeedback(true)} data-testid="header-feedback-btn" title="تقييم وملاحظات">
              <Star size={16} /> <span className="hidden sm:inline">تقييم</span>
            </button>
            {(user?.email || "").toLowerCase() === "admin@hayatak.app" && (
              <Link to="/admin" className="btn-ghost btn" data-testid="header-admin-btn" title="لوحة الإدارة">
                <Shield size={16} /> <span className="hidden sm:inline">{t("admin_panel")}</span>
              </Link>
            )}
            <button className="btn-ghost btn" onClick={logout} data-testid="logout-btn">
              <LogOut size={16} /> {t("logout")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 fade-up">
        <AppointmentReminderBanner />
        {children}
      </main>

      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </div>
  );
}

// ---------- Feedback Modal ----------
function FeedbackModal({ onClose }) {
  const { authHeaders } = useAuth();
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!message.trim()) { setErr("اكتب ملاحظتك من فضلك"); return; }
    setSending(true); setErr("");
    try {
      await axios.post(`${API}/feedback`, { rating, message: message.trim(), category }, { headers: authHeaders });
      setDone(true);
      setTimeout(onClose, 1800);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "تعذّر الإرسال");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} onClick={onClose} data-testid="feedback-modal">
      <div className="glass rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" style={{ background: "linear-gradient(160deg, #2a1f17, #1a1410)", color: "#FAF6F0", border: "1px solid rgba(232,197,71,0.3)" }} onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-6" data-testid="feedback-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Check size={32} style={{ color: "#16a34a" }} />
            </div>
            <h3 className="font-black text-xl mb-1">شكراً لك! 💛</h3>
            <p className="text-sm text-stone-600">وصلنا تقييمك. رأيك يهمنا ويساعدنا على التحسين.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star size={20} style={{ color: "var(--primary)" }} fill="currentColor" />
                <h3 className="font-bold text-lg">شاركنا رأيك</h3>
              </div>
              <button type="button" className="btn-ghost btn text-xs" onClick={onClose} data-testid="feedback-close"><X size={14}/></button>
            </div>
            <p className="text-sm text-stone-600">رأيك يساعدنا نطوّر "حياتك" أكثر. كل ملاحظة وصلتنا تُقرأ.</p>

            {/* Stars */}
            <div>
              <span className="text-sm text-stone-600 block mb-2">تقييمك للتطبيق</span>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(i => (
                  <button type="button" key={i}
                    onClick={() => setRating(i)}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(0)}
                    className="transition-transform"
                    style={{ transform: (hover || rating) >= i ? "scale(1.1)" : "scale(1)" }}
                    data-testid={`feedback-star-${i}`}>
                    <Star size={36} fill={(hover || rating) >= i ? "#f59e0b" : "none"} stroke="#f59e0b" strokeWidth={2}/>
                  </button>
                ))}
              </div>
            </div>

            {/* Category */}
            <div>
              <span className="text-sm text-stone-600 block mb-2">نوع الملاحظة</span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { v: "praise",  l: "💛 إعجاب", c: "#16a34a" },
                  { v: "feature", l: "💡 اقتراح", c: "#0ea5e9" },
                  { v: "bug",     l: "🐛 مشكلة", c: "#dc2626" },
                  { v: "general", l: "📝 عام",    c: "#78716c" },
                ].map(o => {
                  const active = category === o.v;
                  return (
                    <button type="button" key={o.v} onClick={() => setCategory(o.v)}
                      className="px-2 py-2 rounded-xl text-xs font-bold border transition-colors"
                      style={{ background: active ? o.c : "white", color: active ? "white" : o.c, borderColor: o.c }}
                      data-testid={`feedback-cat-${o.v}`}>
                      {o.l}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Message */}
            <label className="block">
              <span className="text-sm text-stone-600">ملاحظتك</span>
              <textarea className="input w-full mt-1" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="شاركنا أي شيء تحبه أو تقترحه أو مشكلة واجهتها..." required data-testid="feedback-message-input"/>
            </label>

            {err && <div className="text-red-600 text-sm" data-testid="feedback-error">{err}</div>}

            <button type="submit" className="btn w-full" disabled={sending} data-testid="feedback-submit-btn">
              {sending ? "جارٍ الإرسال…" : <><Mail size={16}/> أرسل التقييم</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------- Landing / Welcome ----------
function Welcome() {
  const t = useT();
  return (
    <div className="grain min-h-screen">
      <ThemeManager age={25} mood="neutral" />
      <div className="mood-bg" />
      <div className="max-w-5xl mx-auto px-6 py-10 sm:py-16">
        <nav className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-2">
            <Sparkles size={26} style={{ color: "var(--primary)" }} />
            <span className="text-2xl font-extrabold">{t("app_name")}</span>
          </div>
          <div className="flex gap-2 items-center">
            <LanguageSwitcher />
            <InstallButton />
            <Link to="/login" className="btn-ghost btn" data-testid="nav-login-btn"><LogIn size={16}/> {t("nav_login")}</Link>
            <Link to="/register" className="btn" data-testid="nav-register-btn"><UserPlus size={16}/> {t("nav_register")}</Link>
          </div>
        </nav>

        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <h1 className="text-4xl sm:text-6xl font-black leading-tight mb-4">
              {t("tagline_part1")} <span style={{ color: "var(--primary)" }}>{t("tagline_part2")}</span> ✨
            </h1>
            <p className="text-lg text-stone-600 mb-6 leading-relaxed">
              {t("hero_desc")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="btn" data-testid="hero-cta-btn">
                <Sparkles size={18} /> {t("cta_start")}
              </Link>
              <Link to="/login" className="btn-ghost btn">{t("cta_have_account")}</Link>
            </div>

            <div className="mt-6 glass rounded-2xl p-4 text-sm leading-relaxed text-stone-700">
              <div className="flex items-center gap-2 font-bold mb-1">
                <span>🔒</span> {t("safety_title")}
              </div>
              {t("safety_desc")}
            </div>

            <div className="mt-6 flex gap-4 text-sm text-stone-500">
              <Link to="/privacy" className="hover:text-stone-800 underline" data-testid="footer-privacy-link">
                {t("app_name") === "حياتك" ? "سياسة الخصوصية" : "Privacy Policy"}
              </Link>
              <Link to="/terms" className="hover:text-stone-800 underline" data-testid="footer-terms-link">
                {t("app_name") === "حياتك" ? "شروط الاستخدام" : "Terms of Service"}
              </Link>
            </div>
          </div>

          <div className="relative float">
            <div className="glass rounded-3xl p-6 shadow-xl" style={{ boxShadow: "0 20px 60px rgba(232,197,71,0.25)" }}>
              <div className="text-sm text-stone-500 mb-2">{t("companion_says")}</div>
              <div className="bubble-ai">
                {t("sample_msg")}
              </div>
              <div className="flex justify-end mt-3">
                <div className="bubble-user">{t("sample_reply")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ============ Features section (poster-style) ============ */}
        <LandingFeatures />

        {/* ============ About / حول التطبيق ============ */}
        <AboutSection />

      </div>
    </div>
  );
}

// ---------- Landing Features (poster-style) ----------
function LandingFeatures() {
  const features = [
    { icon: MessageCircle, title: "محادثة صادقة",  desc: "يسمعك ويرد بأسلوبك",   grad: "linear-gradient(135deg, #C19A6B, #8B5E3C)" },
    { icon: Calendar,      title: "جدول مواعيدي", desc: "تذكيرات ذكية لكل موعد", grad: "linear-gradient(135deg, #0ea5e9, #0284c7)" },
    { icon: BookOpen,      title: "يومياتك",       desc: "سجل لحظاتك بأمان",      grad: "linear-gradient(135deg, #f59e0b, #d97706)" },
    { icon: Heart,         title: "متتبع المزاج",  desc: "رحلة صحتك النفسية",    grad: "linear-gradient(135deg, #ec4899, #db2777)" },
    { icon: Target,        title: "أهدافك",        desc: "حقّق ما تطمح إليه",    grad: "linear-gradient(135deg, #8b5cf6, #7c3aed)" },
    { icon: BarChart3,     title: "تحليل أسبوعي",  desc: "ملخص ذكي بنصائح",      grad: "linear-gradient(135deg, #14b8a6, #0d9488)" },
    { icon: ImageIcon,     title: "توليد الصور",   desc: "من خيالك بالعربية",    grad: "linear-gradient(135deg, #f43f5e, #e11d48)" },
    { icon: FileText,      title: "تحليل PDF",     desc: "اسأل عن أي ملف",         grad: "linear-gradient(135deg, #6366f1, #4f46e5)" },
    { icon: Search,        title: "بحث ذكي",       desc: "ويب ويوتيوب فورياً",   grad: "linear-gradient(135deg, #84cc16, #65a30d)" },
  ];

  const trustBar = [
    { emoji: "🔒", title: "خصوصية تامة",    sub: "بياناتك مشفّرة" },
    { emoji: "🌍", title: "16 لغة",         sub: "عربي · إنجليزي · تركي…" },
    { emoji: "📱", title: "يعمل كتطبيق",   sub: "بدون متجر" },
    { emoji: "🇮🇶", title: "تصميم عراقي",   sub: "صُنع بحب" },
  ];

  return (
    <section className="mt-20" data-testid="landing-features">
      {/* Featured: صحتي */}
      <div
        className="relative rounded-3xl p-7 sm:p-10 mb-12 fade-up"
        style={{
          background: "linear-gradient(135deg, rgba(232,197,71,0.12), rgba(22,163,74,0.10))",
          border: "2px solid rgba(232,197,71,0.35)",
        }}
      >
        <span
          className="absolute -top-3 right-6 px-3 py-1 rounded-full text-xs font-black"
          style={{ background: "#E8C547", color: "#2a1f17" }}
        >✨ جديد</span>
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          <div
            className="w-24 h-24 rounded-3xl flex items-center justify-center shrink-0"
            style={{
              background: "linear-gradient(135deg, #16a34a, #059669)",
              boxShadow: "0 12px 30px rgba(22,163,74,0.45)",
            }}
          >
            <HeartPulse size={52} color="#fff" />
          </div>
          <div className="flex-1 text-center sm:text-start">
            <h3 className="text-2xl sm:text-3xl font-black mb-2" style={{ color: "#FAF6F0" }}>
              صحتي · مدرّبك الشخصي بالذكاء
            </h3>
            <p className="text-base leading-relaxed mb-4" style={{ color: "rgba(250,246,240,0.78)" }}>
              أدخل طولك ووزنك وحالتك الصحية — يحلّل بياناتك ويعطيك خطة تمارين وغذاء مفصّلة، خطة أسبوعية كاملة، ومتابعة لتقدّمك.
            </p>
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              {["💪 تمارين شخصية", "🥗 خطة غذائية", "📊 متابعة الوزن", "🧠 BMI تلقائي"].map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(232,197,71,0.35)",
                    color: "#E8C547",
                  }}
                >{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section title */}
      <div className="flex items-center gap-3 mb-6 fade-up">
        <span className="w-2 h-8 rounded-full" style={{ background: "#E8C547" }}></span>
        <h2 className="text-2xl sm:text-3xl font-black" style={{ color: "#FAF6F0" }}>
          كل ما تحتاجه في تطبيق واحد
        </h2>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-12">
        {features.map((f, i) => (
          <div
            key={i}
            className="rounded-2xl p-5 flex flex-col items-center text-center transition-transform hover:-translate-y-1 fade-up"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(232,197,71,0.18)",
              backdropFilter: "blur(10px)",
              animationDelay: `${i * 50}ms`,
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
              style={{ background: f.grad }}
            >
              <f.icon size={28} color="#fff" />
            </div>
            <div className="font-extrabold text-base mb-1" style={{ color: "#FAF6F0" }}>{f.title}</div>
            <div className="text-xs" style={{ color: "rgba(250,246,240,0.6)" }}>{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Trust bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {trustBar.map((t, i) => (
          <div
            key={i}
            className="rounded-2xl p-4 flex items-center gap-3"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <div className="text-2xl">{t.emoji}</div>
            <div>
              <div className="font-bold text-sm" style={{ color: "#FAF6F0" }}>{t.title}</div>
              <div className="text-xs" style={{ color: "rgba(250,246,240,0.55)" }}>{t.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- About Section ----------
function AboutSection() {
  const t = useT();
  const security = [
    { icon: Lock,   k: "about_sec_aes" },
    { icon: Shield, k: "about_sec_jwt" },
    { icon: Server, k: "about_sec_isolation" },
    { icon: Eye,    k: "about_sec_privacy" },
  ];
  const engines = [
    { icon: Brain,    k: "about_eng_claude" },
    { icon: Cpu,      k: "about_eng_gpt" },
    { icon: ImageIcon, k: "about_eng_gemini" },
    { icon: Search,   k: "about_eng_search" },
    { icon: Sparkles, k: "about_eng_memory" },
    { icon: BarChart3, k: "about_eng_insights" },
  ];
  const goals = [
    { icon: Heart,   k: "about_goal_companion" },
    { icon: Target,  k: "about_goal_organize" },
    { icon: Award,   k: "about_goal_growth" },
    { icon: Languages, k: "about_goal_languages" },
  ];
  const replaces = [
    "about_rep_journal", "about_rep_mood", "about_rep_planner",
    "about_rep_chatbot", "about_rep_imgedit", "about_rep_pdf",
    "about_rep_search", "about_rep_meditation",
  ];

  return (
    <section className="mt-20 fade-up" id="about" data-testid="about-section">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 text-amber-800 text-xs font-bold mb-3 border border-amber-200">
          <Sparkles size={12} /> {t("about_kicker")}
        </div>
        <h2 className="text-3xl sm:text-5xl font-black mb-3">{t("about_title")}</h2>
        <p className="max-w-2xl mx-auto text-stone-600 leading-relaxed">{t("about_intro")}</p>
      </div>

      {/* Goals */}
      <div className="glass rounded-3xl p-6 sm:p-8 mb-6" data-testid="about-goals">
        <div className="flex items-center gap-2 mb-5">
          <Award size={22} style={{ color: "var(--primary)" }} />
          <h3 className="text-xl font-bold">{t("about_goals_title")}</h3>
        </div>
        <p className="text-stone-700 leading-relaxed mb-5">{t("about_goals_desc")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {goals.map((g, idx) => {
            const Icon = g.icon;
            return (
              <div key={idx} className="rounded-2xl p-4 bg-white/70 border border-stone-200/70 hover:border-amber-300 transition">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-2">
                  <Icon size={18} style={{ color: "var(--primary)" }} />
                </div>
                <div className="text-sm font-bold mb-1">{t(`${g.k}_title`)}</div>
                <div className="text-xs text-stone-600 leading-relaxed">{t(`${g.k}_desc`)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Security + Engines side by side */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Security */}
        <div className="glass rounded-3xl p-6 sm:p-8" data-testid="about-security">
          <div className="flex items-center gap-2 mb-5">
            <Shield size={22} style={{ color: "var(--primary)" }} />
            <h3 className="text-xl font-bold">{t("about_security_title")}</h3>
          </div>
          <p className="text-stone-700 leading-relaxed mb-5">{t("about_security_desc")}</p>
          <ul className="space-y-3">
            {security.map((s, idx) => {
              const Icon = s.icon;
              return (
                <li key={idx} className="flex gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Icon size={16} className="text-emerald-700" />
                  </div>
                  <div>
                    <div className="text-sm font-bold">{t(`${s.k}_title`)}</div>
                    <div className="text-xs text-stone-600 leading-relaxed">{t(`${s.k}_desc`)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* AI Engines */}
        <div className="glass rounded-3xl p-6 sm:p-8" data-testid="about-engines">
          <div className="flex items-center gap-2 mb-5">
            <Cpu size={22} style={{ color: "var(--primary)" }} />
            <h3 className="text-xl font-bold">{t("about_engines_title")}</h3>
          </div>
          <p className="text-stone-700 leading-relaxed mb-5">{t("about_engines_desc")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {engines.map((e, idx) => {
              const Icon = e.icon;
              return (
                <div key={idx} className="rounded-xl p-3 bg-white/70 border border-stone-200/70">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={16} style={{ color: "var(--primary)" }} />
                    <div className="text-sm font-bold">{t(`${e.k}_title`)}</div>
                  </div>
                  <div className="text-xs text-stone-600 leading-relaxed">{t(`${e.k}_desc`)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Replaces */}
      <div className="glass rounded-3xl p-6 sm:p-8 mb-6" data-testid="about-replaces">
        <div className="flex items-center gap-2 mb-3">
          <Layers size={22} style={{ color: "var(--primary)" }} />
          <h3 className="text-xl font-bold">{t("about_replaces_title")}</h3>
        </div>
        <p className="text-stone-700 leading-relaxed mb-5">{t("about_replaces_desc")}</p>
        <div className="flex flex-wrap gap-2">
          {replaces.map((r, idx) => (
            <span key={idx} className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 text-xs font-semibold border border-stone-200">
              {t(r)}
            </span>
          ))}
        </div>
      </div>

      {/* Power highlight */}
      <div className="rounded-3xl p-8 text-center text-white"
        style={{ background: "linear-gradient(135deg, #8B5E3C 0%, #C68B59 50%, #D4A373 100%)" }}
        data-testid="about-power">
        <Zap size={36} className="mx-auto mb-3 opacity-90" />
        <h3 className="text-2xl sm:text-3xl font-black mb-3">{t("about_power_title")}</h3>
        <p className="max-w-2xl mx-auto leading-relaxed opacity-95">{t("about_power_desc")}</p>
        <Link to="/register" className="inline-flex items-center gap-2 mt-6 bg-white text-amber-900 px-6 py-3 rounded-full font-bold hover:scale-105 transition"
          data-testid="about-cta-btn">
          <Sparkles size={16} /> {t("cta_start")}
        </Link>
      </div>

      {/* Iraqi design tag */}
      <div className="text-center mt-10 pt-6 border-t border-stone-200/60">
        <div className="inline-flex items-center gap-2 text-sm text-stone-600 font-semibold" data-testid="iraqi-design-tag">
          <span className="text-lg">🇮🇶</span>
          <span>{t("iraqi_design")}</span>
          <Heart size={14} className="text-red-500" />
        </div>
      </div>
    </section>
  );
}

// ---------- Auth Pages ----------
function formatError(detail) {
  if (!detail) return "حدث خطأ، حاول مرة أخرى";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
}

// Direct Google OAuth — use a RELATIVE URL so the request goes to the same host the user is on
// (yourlife.social on production, preview domain in dev). This guarantees Google's redirect_uri
// matches the user's current domain and shows the right app name on the consent screen.
async function startGoogleLogin() {
  try {
    const probe = await axios.get(`/api/auth/google/start`, { maxRedirects: 0, validateStatus: () => true });
    if (probe.status === 503) {
      window.alert(probe.data?.detail || "ربط جوجل غير مهيأ بعد.");
      return;
    }
    window.location.href = `/api/auth/google/start`;
  } catch {
    window.location.href = `/api/auth/google/start`;
  }
}

async function startFacebookLogin() {
  // Backend builds the FB redirect; if not configured it returns 503 with an Arabic message we surface.
  try {
    const probe = await axios.get(`${API}/auth/facebook/start`, { maxRedirects: 0, validateStatus: () => true });
    if (probe.status >= 300 && probe.status < 400) {
      // axios already navigated browser-side? Fallback to a direct nav.
      window.location.href = `${API}/auth/facebook/start`;
      return;
    }
    if (probe.status === 503) {
      window.alert(probe.data?.detail || "ربط فيسبوك غير مهيأ بعد. سيتم تفعيله قريباً.");
      return;
    }
    // Any other status — just navigate directly (browser follows the redirect natively).
    window.location.href = `${API}/auth/facebook/start`;
  } catch {
    window.location.href = `${API}/auth/facebook/start`;
  }
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { saveAuth } = useAuth();
  const t = useT();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      saveAuth(data.user, data.token);
      nav("/app");
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title={t("welcome_back")} subtitle={t("login_sub")}>
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <input className="input" type="email" placeholder={t("email")}
          value={email} onChange={e=>setEmail(e.target.value)} required data-testid="login-email-input"/>
        <input className="input" type="password" placeholder={t("password")}
          value={password} onChange={e=>setPassword(e.target.value)} required data-testid="login-password-input"/>
        {err && <div className="text-red-600 text-sm" data-testid="login-error">{err}</div>}
        <button className="btn w-full" disabled={loading} data-testid="login-submit-btn">
          {loading ? "..." : t("login")}
        </button>
      </form>

      <div className="my-4 flex items-center gap-2 text-xs text-stone-400">
        <div className="flex-1 h-px bg-stone-200"/> {t("or")} <div className="flex-1 h-px bg-stone-200"/>
      </div>
      <div className="grid grid-cols-1 gap-2">
        <button type="button" className="btn-ghost btn" onClick={startGoogleLogin} data-testid="login-google-btn">
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          {t("google")}
        </button>
        {/* Facebook login button hidden until Facebook app is approved for Live mode.
            Backend OAuth flow remains active and can be re-enabled by restoring this button.
        <button type="button" className="btn-ghost btn" onClick={startFacebookLogin} data-testid="login-facebook-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12z"/></svg>
          {t("facebook")}
        </button>
        */}
      </div>
      <div className="text-center mt-4 text-sm">
        {t("no_account")} <Link to="/register" className="font-bold" style={{color:"var(--primary)"}}>{t("create_account")}</Link>
        <div className="mt-2">
          <Link to="/forgot-password" className="text-stone-500 hover:underline" data-testid="forgot-password-link">
            {t("forgot_password")}
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const t = useT();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setResetToken("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setMsg(data.message || "OK");
      if (data.reset_token) setResetToken(data.reset_token);
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title={t("reset_title")} subtitle={t("reset_sub")}>
      <form onSubmit={submit} className="space-y-3" data-testid="forgot-password-form">
        <input className="input" type="email" placeholder={t("email")}
          value={email} onChange={e=>setEmail(e.target.value)} required
          data-testid="forgot-email-input"/>
        {err && <div className="text-red-600 text-sm" data-testid="forgot-error">{err}</div>}
        {msg && <div className="text-green-700 text-sm bg-green-50 rounded-xl p-3" data-testid="forgot-success">{msg}</div>}
        {resetToken && (
          <div className="bg-stone-50 rounded-xl p-3 text-xs space-y-2" data-testid="forgot-token-box">
            <div className="font-bold">{t("reset_token_label")}</div>
            <div className="font-mono break-all bg-white p-2 rounded border border-stone-200" data-testid="reset-token-value">{resetToken}</div>
            <Link to={`/reset-password?token=${resetToken}`} className="btn w-full mt-2" data-testid="goto-reset-btn">
              {t("proceed_reset")}
            </Link>
          </div>
        )}
        <button className="btn w-full" disabled={loading} data-testid="forgot-submit-btn">
          {loading ? "..." : t("send_reset")}
        </button>
      </form>
      <div className="text-center mt-4 text-sm">
        <Link to="/login" className="font-bold" style={{color:"var(--primary)"}}>{t("back_to_login")}</Link>
      </div>
    </AuthLayout>
  );
}

function ResetPassword() {
  const [token, setToken] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const t = useT();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tk = params.get("token");
    if (tk) setToken(tk);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (pwd !== pwd2) { setErr(t("passwords_dont_match")); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setMsg(data.message || "OK");
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title={t("reset_new_title")} subtitle={t("reset_new_sub")}>
      <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
        <input className="input font-mono text-xs" placeholder={t("reset_token_input")}
          value={token} onChange={e=>setToken(e.target.value)} required
          data-testid="reset-token-input"/>
        <input className="input" type="password" placeholder={t("new_password")}
          minLength={6} value={pwd} onChange={e=>setPwd(e.target.value)} required
          data-testid="reset-password-input"/>
        <input className="input" type="password" placeholder={t("confirm_password")}
          minLength={6} value={pwd2} onChange={e=>setPwd2(e.target.value)} required
          data-testid="reset-password-confirm-input"/>
        {err && <div className="text-red-600 text-sm" data-testid="reset-error">{err}</div>}
        {msg && <div className="text-green-700 text-sm bg-green-50 rounded-xl p-3" data-testid="reset-success">{msg}</div>}
        <button className="btn w-full" disabled={loading} data-testid="reset-submit-btn">
          {loading ? "..." : t("update_password")}
        </button>
      </form>
      <div className="text-center mt-4 text-sm">
        <Link to="/login" className="font-bold" style={{color:"var(--primary)"}}>{t("back_to_login")}</Link>
      </div>
    </AuthLayout>
  );
}

function Register() {
  const { lang: currentLang } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", age: 25, gender: "", country: "", interests: "", companion_name: "", language: currentLang });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { saveAuth } = useAuth();
  const t = useT();
  const nav = useNavigate();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age),
        gender: form.gender || "other",
        companion_name: form.companion_name || "صديقي",
        language: form.language || "ar",
      };
      const { data } = await axios.post(`${API}/auth/register`, payload);
      saveAuth(data.user, data.token);
      nav("/app");
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title={t("start_journey")} subtitle={t("register_sub")}>
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <input className="input" placeholder={t("your_name")}
          value={form.name} onChange={e=>set("name", e.target.value)} required data-testid="register-name-input"/>
        <input className="input" type="email" placeholder={t("your_email")}
          value={form.email} onChange={e=>set("email", e.target.value)} required data-testid="register-email-input"/>
        <input className="input" type="password" placeholder={t("your_password")}
          minLength={6} value={form.password} onChange={e=>set("password", e.target.value)} required data-testid="register-password-input"/>
        <div className="grid grid-cols-2 gap-3">
          <input className="input" type="number" min="10" max="120" placeholder={t("your_age")}
            value={form.age} onChange={e=>set("age", e.target.value)} required data-testid="register-age-input"/>
          <select className="input" value={form.gender} onChange={e=>set("gender", e.target.value)}
            required data-testid="register-gender-input">
            <option value="">{t("gender")}</option>
            <option value="male">{t("male")}</option>
            <option value="female">{t("female")}</option>
            <option value="other">{t("other")}</option>
          </select>
        </div>
        <input className="input" placeholder={t("your_country")}
          value={form.country} onChange={e=>set("country", e.target.value)} data-testid="register-country-input"/>
        <input className="input" placeholder={t("your_interests")}
          value={form.interests} onChange={e=>set("interests", e.target.value)} data-testid="register-interests-input"/>
        <input className="input" placeholder={t("companion_label")}
          value={form.companion_name} onChange={e=>set("companion_name", e.target.value)} data-testid="register-companion-input"/>
        <select className="input" value={form.language} onChange={e=>set("language", e.target.value)}
          data-testid="register-language-input">
          {LANGUAGES.map(l => (
            <option key={l.code} value={l.code}>{l.name} ({t("preferred_language")})</option>
          ))}
        </select>
        <div className="text-xs text-stone-500 bg-stone-50/70 rounded-xl p-3 leading-relaxed">
          {t("privacy_note")}
          <br/>
          {t("safety_note")}
        </div>
        {err && <div className="text-red-600 text-sm" data-testid="register-error">{err}</div>}
        <button className="btn w-full" disabled={loading} data-testid="register-submit-btn">
          {loading ? "..." : t("create_my_account")}
        </button>
      </form>
      <div className="text-center mt-4 text-sm">
        {t("have_account")} <Link to="/login" className="font-bold" style={{color:"var(--primary)"}}>{t("sign_in")}</Link>
      </div>
    </AuthLayout>
  );
}

function AuthLayout({ title, subtitle, children }) {
  const t = useT();
  return (
    <div className="grain min-h-screen flex items-center justify-center px-4">
      <ThemeManager age={25} mood="neutral" />
      <div className="mood-bg" />
      <div className="absolute top-4 end-4 z-10">
        <LanguageSwitcher />
      </div>
      <div className="glass rounded-3xl p-7 sm:p-9 w-full max-w-md fade-up" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.06)" }}>
        <div className="flex items-center gap-2 justify-center mb-5">
          <Sparkles size={26} style={{ color: "var(--primary)" }} />
          <span className="text-2xl font-extrabold">{t("app_name")}</span>
        </div>
        <h2 className="text-2xl font-bold text-center mb-1">{title}</h2>
        <p className="text-center text-stone-600 mb-6 text-sm">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

// ---------- Dashboard ----------
function Dashboard() {
  const { user, authHeaders, lang } = useAuth();
  const [mood, setMood] = useState("neutral");
  const [tab, setTab] = useState("chat");
  const [affirmation, setAffirmation] = useState("");
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  const t = useT();

  useEffect(() => {
    axios.get(`${API}/mood`, { headers: authHeaders }).then(({data}) => {
      if (data.moods?.[0]) setMood(data.moods[0].mood);
    }).catch(()=>{});
    axios.get(`${API}/affirmation`, { headers: authHeaders }).then(({data}) => {
      setAffirmation(data.message || "");
    }).catch(()=>{});
  }, [authHeaders, lang]);

  return (
    <Shell mood={mood} onMoodChange={setMood}>
      {/* Greeting + affirmation */}
      <section className="glass rounded-3xl p-5 sm:p-7 mb-6" data-testid="greeting-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-sm text-stone-500">{t("hello")}</div>
            <div className="text-2xl sm:text-3xl font-extrabold">{user?.name} 💛</div>
            <div className="mt-2 text-stone-700 leading-relaxed">{affirmation || "..."}</div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="mood-selector">
            {MOODS.map(m => {
              const Icon = m.icon;
              return (
                <button key={m.key}
                  className="mood-chip"
                  data-active={mood === m.key}
                  data-testid={`mood-chip-${m.key}`}
                  onClick={async () => {
                    setMood(m.key);
                    try { await axios.post(`${API}/mood`, { mood: m.key }, { headers: authHeaders }); } catch {}
                  }}>
                  <Icon size={16} /> {moodLabel(lang, m.key)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <nav className="glass rounded-full p-1.5 inline-flex gap-1 mb-6 flex-wrap" data-testid="tabs-nav">
        {[
          { k: "chat",     labelKey: "tab_chat",     icon: MessageCircle },
          { k: "voice",    labelKey: "tab_voice",    icon: Mic },
          { k: "journal",  labelKey: "tab_journal",  icon: BookOpen },
          { k: "mood",     labelKey: "tab_mood",     icon: Heart },
          { k: "goals",    labelKey: "tab_goals",    icon: Target },
          { k: "appointments", labelKey: "tab_appointments", icon: Calendar },
          { k: "health",   labelKey: "tab_health",   icon: HeartPulse },
          { k: "weekly",   labelKey: "tab_weekly",   icon: BarChart3 },
          { k: "insights", labelKey: "tab_insights", icon: Sparkles },
          { k: "memories", labelKey: "tab_memories", icon: Brain },
          { k: "images",   labelKey: "tab_images",   icon: ImageIcon },
        ].map(item => {
          const Icon = item.icon;
          const isVoice = item.k === "voice";
          return (
            <button key={item.k}
              className="tab"
              data-active={isVoice ? voiceModeOpen : tab === item.k}
              data-testid={`tab-${item.k}`}
              onClick={() => {
                if (isVoice) {
                  setTab("chat");
                  setVoiceModeOpen(true);
                } else {
                  setTab(item.k);
                }
              }}>
              <Icon size={16} /> {t(item.labelKey)}
            </button>
          );
        })}
      </nav>

      {tab === "chat"     && <ChatPanel mood={mood} onOpenVoice={() => setVoiceModeOpen(true)} />}
      {tab === "journal"  && <JournalPanel currentMood={mood} />}
      {tab === "mood"     && <MoodPanel />}
      {tab === "goals"    && <GoalsPanel />}
      {tab === "appointments" && <AppointmentsPanel />}
      {tab === "health"   && <HealthPanel />}
      {tab === "weekly"   && <WeeklyPanel />}
      {tab === "insights" && <InsightsPanel />}
      {tab === "memories" && <MemoriesPanel />}
      {tab === "images"   && <ImagesPanel />}

      <VoiceConversationMode
        open={voiceModeOpen}
        onClose={() => setVoiceModeOpen(false)}
        mood={mood}
      />
    </Shell>
  );
}

// ---------- Voice Conversation Mode (ChatGPT-style overlay) ----------
function VoiceConversationMode({ open, onClose, mood }) {
  const { user, authHeaders, lang } = useAuth();
  const t = useT();
  // States: idle | listening | thinking | speaking | error
  const [state, setState] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [audioLevel, setAudioLevel] = useState(0); // 0..1 for orb breathing
  const [lastUserText, setLastUserText] = useState("");
  const [lastAiText, setLastAiText] = useState("");
  // Voice selection
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("hayatak_voice_uri") || "openai:nova");
  const [showVoicePicker, setShowVoicePicker] = useState(false);
  const [voiceFilter, setVoiceFilter] = useState("all"); // all | female | male
  const audioElRef = useRef(null);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const silenceStartRef = useRef(null);
  const speakingStartRef = useRef(null);
  const utteranceRef = useRef(null);
  const stoppedRef = useRef(false); // user closed the overlay
  const recStartTimeRef = useRef(0);

  const VAD_THRESHOLD = 0.025;       // RMS level above which we consider "speaking"
  const SILENCE_DURATION_MS = 1500;  // stop after 1.5s of silence
  const MAX_RECORDING_MS = 30000;    // hard cap 30s

  // Map app lang → BCP-47 for speech synth
  const speakLocale = ({
    ar: "ar-SA", en: "en-US", fr: "fr-FR", es: "es-ES", de: "de-DE",
    tr: "tr-TR", it: "it-IT", pt: "pt-BR", ru: "ru-RU", zh: "zh-CN",
    ja: "ja-JP", ko: "ko-KR", hi: "hi-IN", ur: "ur-PK", fa: "fa-IR",
    id: "id-ID", ckb: "ar-IQ", kmr: "ar-IQ",
  })[lang] || "ar-SA";

  const cleanup = () => {
    cancelAnimationFrame(rafRef.current);
    try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    try { window.speechSynthesis?.cancel(); } catch {}
    try { audioElRef.current?.pause(); audioElRef.current = null; } catch {}
    streamRef.current = null;
    recorderRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    silenceStartRef.current = null;
    speakingStartRef.current = null;
  };

  const exit = () => {
    stoppedRef.current = true;
    cleanup();
    setState("idle");
    setAudioLevel(0);
    onClose?.();
  };

  // ----- Start listening + Voice Activity Detection -----
  const startListening = async () => {
    if (stoppedRef.current) return;
    setErrorMsg("");
    setState("listening");
    chunksRef.current = [];
    silenceStartRef.current = null;
    speakingStartRef.current = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;

      // Audio analysis for VAD + orb animation
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      analyserRef.current = analyser;

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = mr;
      recStartTimeRef.current = Date.now();
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        if (stoppedRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        cancelAnimationFrame(rafRef.current);
        try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
        try { audioCtxRef.current?.close(); } catch {}
        streamRef.current = null;
        // If user actually spoke (we detected level above threshold), process
        if (speakingStartRef.current && blob.size > 1200) {
          processUserAudio(blob);
        } else {
          // Nothing detected, loop back
          if (!stoppedRef.current) setTimeout(startListening, 200);
        }
      };
      mr.start();

      // VAD loop
      const buf = new Uint8Array(analyser.fftSize);
      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(buf);
        // RMS
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setAudioLevel(Math.min(1, rms * 4));

        const now = Date.now();
        if (rms > VAD_THRESHOLD) {
          if (!speakingStartRef.current) speakingStartRef.current = now;
          silenceStartRef.current = null;
        } else {
          if (speakingStartRef.current) {
            if (!silenceStartRef.current) silenceStartRef.current = now;
            if (now - silenceStartRef.current > SILENCE_DURATION_MS) {
              // End of speech detected
              try { mr.state !== "inactive" && mr.stop(); } catch {}
              return;
            }
          }
        }
        if (now - recStartTimeRef.current > MAX_RECORDING_MS) {
          try { mr.state !== "inactive" && mr.stop(); } catch {}
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setErrorMsg(t("voice_permission_denied") + " (انقر على 🔒 بجانب العنوان > السماح للميكروفون)");
      } else if (name === "NotFoundError") {
        setErrorMsg("لم يتم العثور على ميكروفون.");
      } else {
        setErrorMsg(`${t("voice_not_supported")} (${name})`);
      }
      setState("error");
    }
  };

  // ----- Send recorded audio to Whisper + Claude + Speak -----
  const processUserAudio = async (blob) => {
    if (stoppedRef.current) return;
    setState("thinking");
    setAudioLevel(0);
    try {
      // 1) Transcribe
      const form = new FormData();
      form.append("audio", blob, "v.webm");
      form.append("language", lang || "");
      const transRes = await axios.post(`${API}/voice/transcribe`, form, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      const userText = (transRes.data?.text || "").trim();
      if (!userText) {
        // nothing transcribed, go back to listening
        if (!stoppedRef.current) startListening();
        return;
      }
      setLastUserText(userText);

      // 2) Chat to Claude (reuses /api/chat → saves to history & memory)
      const chatRes = await axios.post(`${API}/chat`,
        { message: userText, mood },
        { headers: authHeaders });
      const aiText = chatRes.data?.ai_message?.content || "";
      if (!aiText) {
        setErrorMsg(t("error_retry"));
        setState("error");
        return;
      }
      setLastAiText(aiText);

      // 3) Speak the response
      if (stoppedRef.current) return;
      speakAndContinue(aiText);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || t("error_retry");
      setErrorMsg(typeof detail === "string" ? detail : t("error_retry"));
      setState("error");
    }
  };

  // OpenAI TTS voices (natural human-quality, like ChatGPT). These are the primary choice.
  const OPENAI_VOICES = useMemo(() => ([
    { uri: "openai:nova",    name: "Nova",    gender: "female", desc: "نسائي حيوي ودافئ" },
    { uri: "openai:shimmer", name: "Shimmer", gender: "female", desc: "نسائي مشرق ولطيف" },
    { uri: "openai:coral",   name: "Coral",   gender: "female", desc: "نسائي حميمي" },
    { uri: "openai:fable",   name: "Fable",   gender: "female", desc: "نسائي قصصي معبّر" },
    { uri: "openai:alloy",   name: "Alloy",   gender: "male",   desc: "محايد متوازن" },
    { uri: "openai:echo",    name: "Echo",    gender: "male",   desc: "رجالي هادئ" },
    { uri: "openai:onyx",    name: "Onyx",    gender: "male",   desc: "رجالي عميق ووقور" },
    { uri: "openai:sage",    name: "Sage",    gender: "male",   desc: "رجالي حكيم" },
    { uri: "openai:ash",     name: "Ash",     gender: "male",   desc: "رجالي واضح" },
  ]), []);

  // Strip emojis, markdown, URLs, and standalone English nicknames before speaking
  const cleanForSpeech = (text) => {
    if (!text) return "";
    let s = String(text);
    // Remove URLs
    s = s.replace(/https?:\/\/\S+/g, "");
    // Remove markdown chars
    s = s.replace(/[*_`#>~]/g, "");
    // Strip emoji (most ranges)
    s = s.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{2300}-\u{23FF}\u{1F1E6}-\u{1F1FF}]/gu, "");
    // Skip the literal English word "Admin" if it appears (common in admin demo accounts)
    s = s.replace(/\bAdmin\b/gi, "");
    // Compress whitespace
    s = s.replace(/\s+/g, " ").trim();
    return s;
  };

  // Heuristics to detect voice gender from native voice name (for Web Speech fallback)
  const guessGender = (voice) => {
    if (!voice) return "unknown";
    const n = (voice.name + " " + (voice.voiceURI || "")).toLowerCase();
    if (/\b(female|woman|girl|hoda|hala|laila|salma|reem|maryam|fatima|amira|samira|samantha|karen|moira|tessa|kate|fiona|veena|monica|paulina|yuna|kyoko|li-mu|tingting|naayf|nayf|noura|maged-f|sara|sarah|alia|hadeel|yasmin|zira|aria|jenny|hannah|ayse|filiz)\b/.test(n)) return "female";
    if (/\b(male|man|boy|maged|tarik|naseer|hassan|amir|mahmoud|ali|abdul|alex|daniel|fred|tom|martin|jorge|carlos|paul|david|matej|takumi|hiroshi|liang|chuan|mark|oliver|kareem|salim|omar|yusuf|mehmet|cengiz)\b/.test(n)) return "male";
    return "unknown";
  };

  // Load voices (handle async voice loading in Chrome)
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => {
      const v = window.speechSynthesis.getVoices() || [];
      if (v.length) setVoices(v);
    };
    load();
    const handler = () => load();
    if (window.speechSynthesis.addEventListener) {
      window.speechSynthesis.addEventListener("voiceschanged", handler);
      return () => window.speechSynthesis.removeEventListener("voiceschanged", handler);
    }
    window.speechSynthesis.onvoiceschanged = handler;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  // ----- Speak: OpenAI TTS only (natural human voice). NEVER use Web Speech (robotic) -----
  const speakAndContinue = async (text) => {
    const clean = cleanForSpeech(text);
    if (!clean) {
      if (!stoppedRef.current) startListening();
      return;
    }
    setState("speaking");

    // animate orb during speaking with sine-wave
    const startedAt = Date.now();
    const animSpeak = () => {
      if (stoppedRef.current) return;
      const elapsed = (Date.now() - startedAt) / 1000;
      setAudioLevel(0.4 + 0.3 * Math.sin(elapsed * 4));
      rafRef.current = requestAnimationFrame(animSpeak);
    };
    rafRef.current = requestAnimationFrame(animSpeak);

    const onDone = () => {
      cancelAnimationFrame(rafRef.current);
      setAudioLevel(0);
      if (!stoppedRef.current) setTimeout(startListening, 250);
    };

    // OpenAI TTS — the only path. If it fails, show error (no robotic fallback).
    const voiceName = (voiceURI && voiceURI.startsWith("openai:")) ? voiceURI.split(":")[1] : "nova";
    try {
      const resp = await axios.post(`${API}/voice/speak`,
        { text: clean, voice: voiceName, model: "tts-1", speed: 1.0 },
        { headers: authHeaders, responseType: "blob" });
      if (stoppedRef.current) return;
      const blob = new Blob([resp.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      try { audioElRef.current?.pause(); } catch {}
      const audio = new Audio(url);
      audioElRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); onDone(); };
      audio.onerror = () => { URL.revokeObjectURL(url); onDone(); };
      await audio.play();
    } catch (err) {
      cancelAnimationFrame(rafRef.current);
      setAudioLevel(0);
      const detail = err?.response?.data?.detail || err?.message || "تعذّر توليد الصوت";
      setErrorMsg(typeof detail === "string" ? detail : "تعذّر توليد الصوت");
      setState("error");
    }
  };

  // Tap orb to interrupt assistant and start listening
  const onOrbTap = () => {
    if (state === "speaking") {
      try { window.speechSynthesis.cancel(); } catch {}
      try { audioElRef.current?.pause(); } catch {}
      cancelAnimationFrame(rafRef.current);
      setAudioLevel(0);
      startListening();
    }
  };

  // Effect: open/close
  useEffect(() => {
    if (open) {
      stoppedRef.current = false;
      setErrorMsg("");
      setLastUserText("");
      setLastAiText("");
      // Pre-warm Audio playback within user gesture (critical for iOS Safari)
      // Play a tiny silent audio so the browser permits later <audio>.play() calls
      try {
        const silent = new Audio("data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//FJAUaS6vc/////////////7p+f+gB4cgQQAAAAAaJYRWS//tSxAQEBlhrZbAhAAARLazXMtAAcGYIBoY+UQc7l4PiBSiBSGCEAAAAAAA==");
        silent.volume = 0;
        silent.play().catch(() => {});
      } catch {}
      startListening();
    } else {
      cleanup();
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Voice picker helpers
  const langBase = (speakLocale || "ar-SA").split("-")[0];
  const filteredVoices = useMemo(() => {
    // Browser native voices (Web Speech) — fallback option
    const native = (voices || []).filter(v => v.lang?.startsWith(langBase) || v.lang === speakLocale);
    let list = native.length ? native : (voices || []);
    if (voiceFilter !== "all") {
      list = list.filter(v => guessGender(v) === voiceFilter);
      if (list.length === 0) list = native.length ? native : (voices || []); // graceful fallback
    }
    return list.slice(0, 40);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voices, langBase, speakLocale, voiceFilter]);

  const openaiVoicesFiltered = useMemo(() => {
    if (voiceFilter === "all") return OPENAI_VOICES;
    return OPENAI_VOICES.filter(v => v.gender === voiceFilter);
  }, [voiceFilter, OPENAI_VOICES]);

  const pickVoice = (uri) => {
    setVoiceURI(uri);
    try { localStorage.setItem("hayatak_voice_uri", uri); } catch {}
  };

  const sampleVoice = async (v) => {
    // v can be either a native SpeechSynthesisVoice or our OpenAI voice object
    const sampleByLang = {
      ar: "مرحباً، أنا رفيقك. كيف حالك اليوم؟",
      en: "Hello, I am your companion. How are you today?",
      fr: "Bonjour, je suis votre compagnon. Comment allez-vous?",
      es: "Hola, soy tu compañero. ¿Cómo estás?",
      de: "Hallo, ich bin dein Begleiter.",
      tr: "Merhaba, ben senin arkadaşınım.",
      ckb: "سڵاو، من هاوڕێی تۆم.",
      kmr: "سلاڤ، ئەز هەڤالێ تە مە.",
    };
    const sample = sampleByLang[lang] || sampleByLang.ar;
    // OpenAI voice (has uri starting with openai:)
    if (v?.uri && v.uri.startsWith("openai:")) {
      try {
        const voice = v.uri.split(":")[1];
        const resp = await axios.post(`${API}/voice/speak`,
          { text: sample, voice, model: "tts-1", speed: 1.0 },
          { headers: authHeaders, responseType: "blob" });
        const blob = new Blob([resp.data], { type: "audio/mpeg" });
        const url = URL.createObjectURL(blob);
        try { audioElRef.current?.pause(); } catch {}
        const audio = new Audio(url);
        audioElRef.current = audio;
        audio.onended = () => URL.revokeObjectURL(url);
        await audio.play();
      } catch {}
      return;
    }
    // Native voice
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(sample);
      u.voice = v; u.lang = v.lang;
      window.speechSynthesis.speak(u);
    } catch {}
  };

  if (!open) return null;

  const orbScale = 1 + audioLevel * 0.5;
  const orbColor = state === "speaking" ? "#E8C547"
                : state === "thinking" ? "#9B7BB8"
                : state === "error"    ? "#ef4444"
                : "#7BB8E8";

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-between"
         style={{ background: "radial-gradient(ellipse at center, #2a1f17 0%, #0d0805 70%, #000 100%)" }}
         data-testid="voice-mode-overlay">
      {/* Top bar */}
      <div className="w-full flex items-center justify-between p-4 sm:p-6">
        <div className="flex items-center gap-2 text-amber-200/80">
          <Sparkles size={18} />
          <span className="font-bold">{user?.companion_name || t("personal_companion")}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exit}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white transition shrink-0"
                  title="إغلاق"
                  data-testid="voice-mode-close">
            <X size={20}/>
          </button>
          <button onClick={() => setShowVoicePicker(s => !s)}
                  className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center gap-2 text-amber-100 transition text-sm font-semibold"
                  title={t("voice_settings")}
                  data-testid="voice-mode-picker-toggle">
            <span>{t("voice_pick")}</span>
            <ChevronDown size={16} className={`transition-transform ${showVoicePicker ? "rotate-180" : ""}`}/>
          </button>
        </div>
      </div>

      {/* Voice picker panel */}
      {showVoicePicker && (
        <div className="absolute top-20 inset-x-4 sm:inset-x-auto sm:end-6 sm:w-[440px] max-h-[60vh] z-10 rounded-2xl border border-amber-500/30 backdrop-blur-xl overflow-hidden flex flex-col"
             style={{ background: "rgba(20, 14, 10, 0.95)" }}
             data-testid="voice-picker-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-amber-500/20">
            <div className="text-amber-100 font-bold flex items-center gap-2">
              <Volume2 size={16}/> {t("voice_pick")}
            </div>
            <button onClick={() => setShowVoicePicker(false)}
                    className="text-amber-200/70 hover:text-white p-1"
                    data-testid="voice-picker-close">
              <X size={16}/>
            </button>
          </div>
          {/* Gender filter */}
          <div className="flex gap-1 px-4 py-2 border-b border-amber-500/10">
            {[
              { v: "all", label: lang.startsWith("en") ? "All" : "الكل" },
              { v: "female", label: lang.startsWith("en") ? "♀ Female" : "♀ نسائي" },
              { v: "male", label: lang.startsWith("en") ? "♂ Male" : "♂ رجالي" },
            ].map(o => (
              <button key={o.v} onClick={() => setVoiceFilter(o.v)}
                      className={`text-xs px-3 py-1.5 rounded-full transition ${voiceFilter === o.v ? "bg-amber-400 text-stone-900 font-bold" : "bg-white/10 text-amber-100 hover:bg-white/20"}`}
                      data-testid={`voice-filter-${o.v}`}>
                {o.label}
              </button>
            ))}
          </div>
          {/* Voice list — OpenAI premium voices only (browser native voices removed as they sound robotic) */}
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1" data-testid="voice-picker-list">
            <div className="px-2 py-1.5 text-[11px] font-bold text-amber-300/80 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles size={10}/>
              {lang.startsWith("en") ? "Natural human voices" : "أصوات بشرية طبيعية"}
            </div>
            {openaiVoicesFiltered.map(v => {
              const selected = voiceURI === v.uri;
              return (
                <div key={v.uri}
                     className={`flex items-center gap-2 p-2 rounded-xl cursor-pointer transition ${selected ? "bg-amber-400/25 border border-amber-400/50" : "hover:bg-white/5 border border-transparent"}`}
                     onClick={() => pickVoice(v.uri)}
                     data-testid={`voice-item-${v.uri}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-amber-50 truncate flex items-center gap-1.5">
                      {v.gender === "female" ? <span>♀</span> : <span>♂</span>}
                      <span className="font-semibold">{v.name}</span>
                      {selected && <span className="text-[10px] text-amber-300 ms-1">✓</span>}
                    </div>
                    <div className="text-[11px] text-amber-200/60">{v.desc}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); sampleVoice(v); }}
                          className="px-2.5 py-1 text-[11px] rounded-full bg-amber-400/30 hover:bg-amber-400/50 text-amber-50 font-semibold transition"
                          data-testid={`voice-sample-${v.uri}`}>
                    ▶
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Central orb */}
      <div className="flex-1 flex flex-col items-center justify-center w-full px-6">
        <button onClick={onOrbTap}
                disabled={state !== "speaking"}
                className="relative outline-none p-0 m-0 border-0 bg-transparent"
                style={{
                  cursor: state === "speaking" ? "pointer" : "default",
                  width: "180px", height: "180px",
                  flexShrink: 0,
                  display: "block",
                  aspectRatio: "1 / 1",
                }}
                data-testid="voice-mode-orb">
          {/* Outer halo */}
          <div className="absolute rounded-full pointer-events-none"
               style={{
                 transform: `translate(-50%, -50%) scale(${1.4 + audioLevel * 0.6})`,
                 top: "50%", insetInlineStart: "50%",
                 background: `radial-gradient(circle, ${orbColor}40 0%, transparent 70%)`,
                 transition: "transform 80ms ease-out",
                 filter: "blur(20px)",
                 width: "220px", height: "220px",
                 aspectRatio: "1 / 1",
               }} />
          {/* Ping */}
          <div className="absolute rounded-full animate-ping opacity-30 pointer-events-none"
               style={{
                 background: `radial-gradient(circle, ${orbColor} 0%, transparent 60%)`,
                 top: 0, insetInlineStart: 0,
                 width: "180px", height: "180px",
                 aspectRatio: "1 / 1",
               }} />
          {/* Core orb (perfectly circular) */}
          <div className="absolute rounded-full shadow-2xl"
               style={{
                 top: 0, insetInlineStart: 0,
                 width: "180px", height: "180px",
                 aspectRatio: "1 / 1",
                 background: `radial-gradient(circle at 30% 30%, #fff8dc 0%, ${orbColor} 35%, #5a3812 100%)`,
                 transform: `scale(${orbScale})`,
                 transition: "transform 80ms ease-out, background 400ms ease",
                 boxShadow: `0 0 50px ${orbColor}80, 0 0 100px ${orbColor}40, inset 0 0 60px rgba(255,255,255,0.15)`,
               }}>
            <div className="absolute inset-4 rounded-full opacity-50"
                 style={{
                   background: `conic-gradient(from 0deg, transparent, ${orbColor}80, transparent, ${orbColor}80, transparent)`,
                   animation: state === "thinking" ? "spin 2s linear infinite" : "spin 8s linear infinite",
                 }}/>
            {state === "thinking" && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-white/80 animate-pulse"/>
              </div>
            )}
          </div>
        </button>

        {/* Error display only (no status labels for cleaner UI) */}
        {errorMsg && (
          <div className="mt-8 max-w-md text-center bg-red-500/15 border border-red-500/40 rounded-2xl p-4 text-red-200 text-sm leading-relaxed"
               style={{ direction: "rtl" }}
               data-testid="voice-mode-error">
            <div className="font-bold text-red-100 mb-2">⚠️ تعذّر الوصول للميكروفون</div>
            <div className="text-xs text-red-100/90 mb-3">
              قد تكون في وضع <b>التصفّح الخاص</b> (Private/Incognito). نظام iOS يمنع تشغيل الميكروفون في هذا الوضع.
            </div>
            <div className="text-[11px] text-red-100/70 mb-3 text-start space-y-1" style={{ direction: "rtl" }}>
              <div>• اخرج من التصفّح الخاص واستخدم وضع Safari العادي</div>
              <div>• اضغط على 🔒 بجانب الرابط ← السماح للميكروفون</div>
              <div>• تأكد من السماح في إعدادات iPhone ← Safari ← الميكروفون</div>
            </div>
            <button onClick={() => { setErrorMsg(""); setState("idle"); startListening(); }}
                    className="px-3 py-1.5 rounded-full bg-amber-400 text-stone-900 text-xs font-bold hover:bg-amber-300 transition"
                    data-testid="voice-mode-retry">إعادة المحاولة</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------- Chat Panel ----------
function ChatPanel({ mood, onOpenVoice }) {
  const { user, authHeaders, lang } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [starters, setStarters] = useState([]);
  const [loadingStarters, setLoadingStarters] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { file, previewUrl }
  const [pendingPdf, setPendingPdf] = useState(null); // { file, name, sizeKB }
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState(""); // visible inline error
  const [speakingId, setSpeakingId] = useState(null); // id of currently-spoken AI message
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const t = useT();

  const loadStarters = () => {
    setLoadingStarters(true);
    axios.get(`${API}/conversation-starters`, { headers: authHeaders })
      .then(({ data }) => setStarters(data.starters || []))
      .catch(() => setStarters([]))
      .finally(() => setLoadingStarters(false));
  };

  useEffect(() => {
    axios.get(`${API}/chat/history`, { headers: authHeaders })
      .then(({data}) => setMessages(data.messages || []))
      .catch(()=>{});
    loadStarters();
  }, [authHeaders]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Clean up preview URLs to avoid leaks
  useEffect(() => {
    return () => {
      if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    };
  }, [pendingImage]);

  const handleImagePick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking same file
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      alert(t("image_only"));
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      alert(t("image_too_large"));
      return;
    }
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage({ file: f, previewUrl: URL.createObjectURL(f) });
  };

  const clearPendingImage = () => {
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
  };

  const handlePdfPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isPdf = f.type === "application/pdf" || (f.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      alert(t("pdf_only") || "يرجى اختيار ملف PDF");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      alert(t("pdf_too_big") || "الملف كبير جداً (الحد الأقصى 15 ميغابايت)");
      return;
    }
    // Clear any pending image so only one attachment at a time
    if (pendingImage) clearPendingImage();
    setPendingPdf({ file: f, name: f.name, sizeKB: Math.round(f.size / 1024) });
  };

  const clearPendingPdf = () => setPendingPdf(null);

  const sendPdfMessage = async (file, caption) => {
    setSending(true);
    const tempId = "tmp-" + Date.now();
    const tempUser = {
      id: tempId, role: "user",
      content: caption || `📄 ${file.name}`,
      pdf_name: file.name,
    };
    setMessages(m => [...m, tempUser]);
    try {
      const form = new FormData();
      form.append("pdf", file);
      form.append("message", caption || "");
      form.append("mood", mood || "neutral");
      const { data } = await axios.post(`${API}/chat/pdf`, form, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setMessages(m => [...m.filter(x => x.id !== tempId), data.user_message, data.ai_message]);
    } catch (err) {
      const detail = err?.response?.data?.detail || t("error_retry");
      setMessages(m => [...m.filter(x => x.id !== tempId),
        { id: "err-" + Date.now(), role: "assistant", content: detail }]);
    } finally {
      setSending(false);
    }
  };

  const sendImageMessage = async (file, caption) => {
    setSending(true);
    const tempId = "tmp-" + Date.now();
    const localPreview = URL.createObjectURL(file);
    const tempUser = {
      id: tempId, role: "user",
      content: caption || "ما رأيك في هذه الصورة؟",
      image_url: localPreview,
    };
    setMessages(m => [...m, tempUser]);
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("message", caption || "");
      form.append("mood", mood || "neutral");
      const { data } = await axios.post(`${API}/chat/image`, form, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setMessages(m => [...m.filter(x => x.id !== tempId), data.user_message, data.ai_message]);
    } catch (err) {
      setMessages(m => [...m.filter(x => x.id !== tempId),
        { id: "err-" + Date.now(), role: "assistant", content: t("error_retry") }]);
    } finally {
      URL.revokeObjectURL(localPreview);
      setSending(false);
    }
  };

  const sendText = async (text) => {
    if ((!text.trim() && !pendingImage && !pendingPdf) || sending) return;
    // PDF path (priority over image since both shouldn't coexist)
    if (pendingPdf) {
      const file = pendingPdf.file;
      const caption = text;
      setInput("");
      clearPendingPdf();
      await sendPdfMessage(file, caption);
      return;
    }
    // Image path
    if (pendingImage) {
      const file = pendingImage.file;
      const caption = text;
      setInput("");
      clearPendingImage();
      await sendImageMessage(file, caption);
      return;
    }
    if (!text.trim()) return;
    setInput("");
    setSending(true);
    const tempUser = { id: "tmp-"+Date.now(), role: "user", content: text };
    setMessages(m => [...m, tempUser]);
    try {
      const { data } = await axios.post(`${API}/chat`,
        { message: text, mood }, { headers: authHeaders });
      const aiMsg = (data && data.search && data.search.used === true)
        ? { ...data.ai_message, _search: data.search }
        : data.ai_message;
      setMessages(m => [...m.filter(x => x.id !== tempUser.id), data.user_message, aiMsg]);
    } catch (err) {
      setMessages(m => [...m, { id: "err", role: "assistant", content: t("error_retry") }]);
    } finally { setSending(false); }
  };

  const send = (e) => {
    e?.preventDefault();
    sendText(input.trim());
  };

  const clearHistory = async () => {
    if (!window.confirm(t("confirm_clear"))) return;
    try {
      await axios.delete(`${API}/chat/history`, { headers: authHeaders });
      setMessages([]);
      loadStarters();
    } catch {}
  };

  // ----- Voice recording (Speech-to-text via Whisper) -----
  const startRecording = async () => {
    if (recording || sending || transcribing) return;
    setVoiceError("");
    // Pre-flight checks with specific error messages
    if (!window.isSecureContext) {
      setVoiceError("التسجيل الصوتي يتطلب اتصالاً آمناً (HTTPS).");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError(t("voice_not_supported"));
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setVoiceError(t("voice_not_supported"));
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      // Permission denied or device unavailable
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        setVoiceError(t("voice_permission_denied") + " (انقر على رمز القفل 🔒 بجانب العنوان > السماح للميكروفون)");
      } else if (err?.name === "NotFoundError") {
        setVoiceError("لم يتم العثور على ميكروفون في هذا الجهاز.");
      } else {
        setVoiceError(`${t("voice_not_supported")} (${err?.name || "Error"})`);
      }
      return;
    }
    try {
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(tr => tr.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 800) {
          setVoiceError("التسجيل قصير جداً، تكلّم لثانية أو أكثر.");
          return;
        }
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "recording.webm");
          form.append("language", lang || "");
          const { data } = await axios.post(`${API}/voice/transcribe`, form, {
            headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
          });
          if (data?.text) {
            setInput(prev => (prev ? prev + " " : "") + data.text);
          } else {
            setVoiceError("لم نسمع كلاماً واضحاً، حاول مرة أخرى.");
          }
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || t("error_retry");
          setVoiceError(typeof detail === "string" ? detail : t("error_retry"));
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      try { stream?.getTracks().forEach(tr => tr.stop()); } catch {}
      setVoiceError(`${t("voice_not_supported")} (${err?.message || "MediaRecorder"})`);
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    setRecording(false);
  };

  // ----- TTS: speak AI message aloud via Web Speech API -----
  const speak = (msgId, text) => {
    if (!window.speechSynthesis) return;
    // If already speaking the same message, stop
    if (speakingId === msgId) {
      window.speechSynthesis.cancel();
      setSpeakingId(null);
      return;
    }
    window.speechSynthesis.cancel();
    const clean = (text || "").replace(/https?:\/\/\S+/g, "").trim();
    if (!clean) return;
    const utter = new SpeechSynthesisUtterance(clean);
    // Map app lang → BCP-47 voice locale
    const localeMap = {
      ar: "ar-SA", en: "en-US", fr: "fr-FR", es: "es-ES", de: "de-DE",
      tr: "tr-TR", it: "it-IT", pt: "pt-BR", ru: "ru-RU", zh: "zh-CN",
      ja: "ja-JP", ko: "ko-KR", hi: "hi-IN", ur: "ur-PK", fa: "fa-IR",
      id: "id-ID", ckb: "ar-IQ", kmr: "ar-IQ",
    };
    utter.lang = localeMap[lang] || "ar-SA";
    utter.rate = 1.0; utter.pitch = 1.0;
    // Try to pick a voice matching the locale
    const voices = window.speechSynthesis.getVoices();
    const pref = voices.find(v => v.lang === utter.lang) || voices.find(v => v.lang?.startsWith(utter.lang.split("-")[0]));
    if (pref) utter.voice = pref;
    utter.onend = () => setSpeakingId(null);
    utter.onerror = () => setSpeakingId(null);
    setSpeakingId(msgId);
    window.speechSynthesis.speak(utter);
  };

  // Stop speech when leaving panel
  useEffect(() => {
    return () => { if (window.speechSynthesis) window.speechSynthesis.cancel(); };
  }, []);

  return (
    <div className="glass rounded-3xl flex flex-col" style={{ height: "min(72vh, 700px)" }} data-testid="chat-panel">
      <div className="flex items-center justify-between p-4 border-b border-stone-200/50">
        <div className="flex items-center gap-2">
          <Sparkles size={18} style={{ color: "var(--primary)" }} />
          <div className="font-bold">{user?.companion_name}</div>
          <span className="text-xs text-stone-500">{t("personal_companion")}</span>
        </div>
        <button className="text-xs text-stone-500 hover:text-red-600 flex items-center gap-1"
          onClick={clearHistory} data-testid="chat-clear-btn">
          <Trash2 size={14} /> {t("clear")}
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-stone-500 py-10">
            {t("start_chat")}
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"} fade-up`}>
            <div className={m.role === "user" ? "bubble-user" : "bubble-ai"} data-testid={`msg-${m.role}`}>
              {m._search && m._search.used === true && (
                <div className="flex items-center gap-1 text-[11px] mb-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 w-fit"
                     data-testid="web-search-badge">
                  <Search size={11} />
                  <span>{t("web_search_used")}</span>
                </div>
              )}
              {m.image_url && (
                <a href={m.image_url} target="_blank" rel="noopener noreferrer"
                   className="block mb-2" data-testid="msg-image">
                  <img src={m.image_url} alt={m.role === "user" ? "uploaded by user" : "ai generated"}
                    className="rounded-xl max-h-72 object-cover border border-stone-200/40 hover:opacity-95 transition"
                    style={{ maxWidth: 320 }} />
                </a>
              )}
              {m.pdf_name && (
                <div className="mb-2" data-testid="msg-pdf">
                  {m.pdf_url ? (
                    <a href={m.pdf_url} download={m.pdf_name}
                       className="flex items-center gap-3 bg-gradient-to-br from-amber-400/20 to-amber-600/10 hover:from-amber-400/30 hover:to-amber-600/20 border border-amber-500/40 rounded-2xl p-3 transition group"
                       data-testid="msg-pdf-download">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition">
                        <FileText size={22} className="text-stone-900"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{m.pdf_title || m.pdf_name}</div>
                        <div className="text-[11px] opacity-70 flex items-center gap-1">
                          <Download size={11}/> {t("download") || "تحميل"} · PDF
                        </div>
                      </div>
                    </a>
                  ) : (
                    <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-2">
                      <div className="w-9 h-9 rounded-lg bg-red-500/20 flex items-center justify-center shrink-0">
                        <FileText size={18} className="text-red-700"/>
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold truncate">{m.pdf_name}</div>
                        {m.pdf_pages && <div className="text-[10px] opacity-70">PDF · {m.pdf_pages} {t("pdf_pages") || "صفحة"}</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {m.content && linkify(m.content)}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-end">
            <div className="bubble-ai dots"><span/><span/><span/></div>
          </div>
        )}
      </div>

      {/* Conversation starters */}
      {starters.length > 0 && !pendingImage && (
        <div className="px-3 pt-2 pb-1 border-t border-stone-200/50" data-testid="starters-bar">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs font-bold text-stone-500 flex items-center gap-1">
              <Sparkles size={12} style={{ color: "var(--primary)" }} /> {t("starters_label")}
            </div>
            <button onClick={loadStarters} disabled={loadingStarters}
              className="text-xs text-stone-400 hover:text-stone-700 flex items-center gap-1"
              data-testid="starters-refresh-btn">
              <RefreshCw size={11} className={loadingStarters ? "animate-spin" : ""} /> {t("starters_refresh")}
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }}>
            {starters.map((q, idx) => (
              <button key={idx}
                onClick={() => sendText(q)}
                disabled={sending}
                className="shrink-0 text-sm bg-white/80 border border-stone-200 hover:border-[var(--primary)] hover:bg-white text-stone-700 rounded-full px-3 py-1.5 transition-all"
                style={{ maxWidth: 320 }}
                data-testid={`starter-${idx}`}
                title={q}>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pending image preview */}
      {pendingImage && (
        <div className="px-3 pt-2 border-t border-stone-200/50" data-testid="pending-image-preview">
          <div className="flex items-center gap-3 bg-white/60 rounded-xl p-2 border border-stone-200/60">
            <img src={pendingImage.previewUrl} alt="preview"
                 className="w-14 h-14 rounded-lg object-cover border border-stone-200/60" />
            <div className="flex-1 text-xs text-stone-600 truncate">
              {pendingImage.file.name} • {Math.round(pendingImage.file.size / 1024)} KB
            </div>
            <button type="button"
              onClick={clearPendingImage}
              className="text-stone-500 hover:text-red-600 p-1 rounded-full hover:bg-stone-100 transition"
              title={t("remove_image")}
              data-testid="remove-pending-image-btn">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Pending PDF preview */}
      {pendingPdf && (
        <div className="px-3 pt-2 border-t border-stone-200/50" data-testid="pending-pdf-preview">
          <div className="flex items-center gap-3 bg-white/60 rounded-xl p-2 border border-stone-200/60">
            <div className="w-14 h-14 rounded-lg bg-red-500/15 border border-red-500/40 flex items-center justify-center shrink-0">
              <FileText size={26} className="text-red-600"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-700 truncate" data-testid="pending-pdf-name">
                {pendingPdf.name}
              </div>
              <div className="text-xs text-stone-500">PDF • {pendingPdf.sizeKB} KB</div>
            </div>
            <button type="button"
              onClick={clearPendingPdf}
              className="text-stone-500 hover:text-red-600 p-1 rounded-full hover:bg-stone-100 transition"
              title={t("remove_image")}
              data-testid="remove-pending-pdf-btn">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Voice error banner */}
      {voiceError && (
        <div className="px-3 pt-2" data-testid="voice-error-banner">
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/40 rounded-xl p-2.5 text-sm text-red-200">
            <AlertCircle size={16} className="shrink-0 mt-0.5"/>
            <div className="flex-1 leading-relaxed">{voiceError}</div>
            <button type="button" onClick={() => setVoiceError("")}
                    className="text-red-200 hover:text-white p-0.5 shrink-0"
                    data-testid="voice-error-close">
              <X size={14}/>
            </button>
          </div>
        </div>
      )}

      <form onSubmit={send} className="p-3 border-t border-stone-200/50 flex gap-2 items-center">
        {/* Hidden inputs */}
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
               onChange={handleImagePick} data-testid="chat-image-input"/>
        <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" className="hidden"
               onChange={handlePdfPick} data-testid="chat-pdf-input"/>

        {/* + Attach menu (image / camera / pdf) */}
        <div className="relative shrink-0">
          <button type="button"
                  onClick={() => setAttachMenuOpen(s => !s)}
                  disabled={sending}
                  className="w-9 h-9 rounded-full bg-white/70 hover:bg-white border border-stone-200 hover:border-[var(--primary)] flex items-center justify-center text-stone-700 hover:text-[var(--primary)] transition disabled:opacity-50"
                  title={t("attach_image")}
                  data-testid="chat-attach-menu-btn"
                  aria-expanded={attachMenuOpen}>
            <Plus size={18} className={`transition-transform ${attachMenuOpen ? "rotate-45" : ""}`} />
          </button>
          {attachMenuOpen && (
            <>
              {/* Click-away backdrop */}
              <div className="fixed inset-0 z-30"
                   onClick={() => setAttachMenuOpen(false)}
                   data-testid="attach-menu-backdrop"/>
              {/* Menu */}
              <div className="absolute bottom-12 start-0 z-40 min-w-[180px] rounded-2xl border border-stone-200/70 backdrop-blur-xl overflow-hidden"
                   style={{ background: "rgba(26, 20, 16, 0.95)" }}
                   data-testid="attach-menu">
                <button type="button"
                  onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-400/15 text-amber-50 text-sm transition"
                  data-testid="attach-menu-image">
                  <ImageIcon size={16} className="text-amber-300"/>
                  <span>{t("attach_image")}</span>
                </button>
                <button type="button"
                  onClick={() => { setAttachMenuOpen(false); pdfInputRef.current?.click(); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-amber-400/15 text-amber-50 text-sm transition border-t border-amber-500/10"
                  data-testid="attach-menu-pdf">
                  <FileText size={16} className="text-amber-300"/>
                  <span>{t("attach_pdf") || "إرفاق PDF"}</span>
                </button>
              </div>
            </>
          )}
        </div>

        <input className="input flex-1" placeholder={pendingPdf ? (t("pdf_question_ph") || "اكتب سؤالك عن الملف...") : (pendingImage ? t("image_caption_placeholder") : t("chat_placeholder"))}
          value={input} onChange={e=>setInput(e.target.value)} disabled={sending}
          data-testid="chat-input"/>
        <button className="btn" disabled={sending || (!input.trim() && !pendingImage && !pendingPdf)} data-testid="chat-send-btn">
          <Send size={16} /> {t("send")}
        </button>
      </form>
    </div>
  );
}

// ---------- Journal Panel ----------
function JournalPanel({ currentMood }) {
  const { authHeaders, lang } = useAuth();
  const [entries, setEntries] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const t = useT();

  const load = () => axios.get(`${API}/journal`, { headers: authHeaders })
    .then(({data}) => setEntries(data.entries || []))
    .catch(()=>{});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/journal`, { title, content, mood: currentMood }, { headers: authHeaders });
      setTitle(""); setContent("");
      load();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/journal/${id}`, { headers: authHeaders });
      load();
    } catch {}
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="journal-panel">
      <form onSubmit={save} className="glass rounded-3xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <BookOpen size={20} style={{color:"var(--primary)"}} />
          <h3 className="text-lg font-bold">{t("journal_title_label")}</h3>
        </div>
        <input className="input" placeholder={t("journal_title_input")}
          value={title} onChange={e=>setTitle(e.target.value)} data-testid="journal-title-input"/>
        <textarea className="input" rows={6} placeholder={t("journal_content_input")}
          value={content} onChange={e=>setContent(e.target.value)} required
          data-testid="journal-content-input"/>
        <button className="btn w-full" disabled={saving || !content.trim()} data-testid="journal-save-btn">
          <Plus size={16} /> {t("add_to_journal")}
        </button>
      </form>

      <div className="space-y-3" data-testid="journal-list">
        {entries.length === 0 && (
          <div className="glass rounded-3xl p-6 text-center text-stone-500">
            {t("no_journal_yet")}
          </div>
        )}
        {entries.map(e => {
          const m = MOODS.find(x => x.key === e.mood) || MOODS[1];
          const Icon = m.icon;
          return (
            <div key={e.id} className="glass rounded-2xl p-5 fade-up" data-testid={`journal-entry-${e.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon size={16} style={{color: m.color}} />
                  <span className="font-bold">{e.title || t("untitled")}</span>
                </div>
                <button onClick={() => remove(e.id)} className="text-stone-400 hover:text-red-600"
                  data-testid={`journal-delete-${e.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="text-stone-700 whitespace-pre-wrap leading-relaxed">{linkify(e.content)}</div>
              <div className="text-xs text-stone-400 mt-2">
                {new Date(e.created_at).toLocaleString(lang === "ar" ? "ar-EG" : lang)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Mood Panel ----------
function MoodPanel() {
  const { authHeaders, lang } = useAuth();
  const [moods, setMoods] = useState([]);
  const [note, setNote] = useState("");
  const [pick, setPick] = useState("neutral");
  const t = useT();

  const load = () => axios.get(`${API}/mood`, { headers: authHeaders })
    .then(({data}) => setMoods(data.moods || []))
    .catch(()=>{});
  useEffect(() => { load(); }, []);

  const log = async () => {
    try {
      await axios.post(`${API}/mood`, { mood: pick, note }, { headers: authHeaders });
      setNote("");
      load();
    } catch {}
  };

  // Compute weekly stats
  const stats = MOODS.map(m => ({
    ...m,
    count: moods.filter(x => x.mood === m.key).length,
  }));
  const total = moods.length || 1;

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="mood-panel">
      <div className="glass rounded-3xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Heart size={20} style={{color:"var(--primary)"}} />
          <h3 className="text-lg font-bold">{t("log_mood_title")}</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {MOODS.map(m => {
            const Icon = m.icon;
            return (
              <button key={m.key} className="mood-chip" data-active={pick === m.key}
                onClick={() => setPick(m.key)} data-testid={`mood-pick-${m.key}`}>
                <Icon size={16} /> {moodLabel(lang, m.key)}
              </button>
            );
          })}
        </div>
        <textarea className="input" rows={3} placeholder={t("mood_note_input")}
          value={note} onChange={e=>setNote(e.target.value)} data-testid="mood-note-input"/>
        <button className="btn w-full" onClick={log} data-testid="mood-log-btn">
          <Plus size={16}/> {t("log")}
        </button>
      </div>

      <div className="glass rounded-3xl p-5">
        <h3 className="text-lg font-bold mb-3">{t("stats_label")} ({moods.length} {t("entries")})</h3>
        <div className="space-y-2">
          {stats.map(s => {
            const pct = Math.round((s.count / total) * 100);
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex items-center gap-3">
                <Icon size={18} style={{color: s.color}} />
                <span className="w-20 font-semibold">{moodLabel(lang, s.key)}</span>
                <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{width: `${pct}%`, background: s.color}} />
                </div>
                <span className="text-xs text-stone-500 w-10 text-left">{pct}%</span>
              </div>
            );
          })}
        </div>

        <h4 className="font-bold mt-6 mb-2">{t("last_logs")}</h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {moods.slice(0, 20).map(m => {
            const md = MOODS.find(x => x.key === m.mood) || MOODS[1];
            const Icon = md.icon;
            return (
              <div key={m.id} className="flex items-center gap-2 text-sm border-b border-stone-100 py-1">
                <Icon size={14} style={{color: md.color}} />
                <span className="font-semibold">{moodLabel(lang, m.mood)}</span>
                {m.note && <span className="text-stone-500">— {m.note}</span>}
                <span className="text-stone-400 text-xs ms-auto">
                  {new Date(m.created_at).toLocaleDateString(lang === "ar" ? "ar-EG" : lang)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Goals Panel ----------
function GoalsPanel() {
  const { authHeaders } = useAuth();
  const [goals, setGoals] = useState([]);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const t = useT();

  const load = () => axios.get(`${API}/goals`, { headers: authHeaders })
    .then(({data}) => setGoals(data.goals || []))
    .catch(()=>{});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await axios.post(`${API}/goals`, { title, description: desc }, { headers: authHeaders });
      setTitle(""); setDesc("");
      load();
    } catch {}
  };

  const toggle = async (g) => {
    try {
      await axios.put(`${API}/goals/${g.id}`, { completed: !g.completed }, { headers: authHeaders });
      load();
    } catch {}
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/goals/${id}`, { headers: authHeaders });
      load();
    } catch {}
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="goals-panel">
      <form onSubmit={add} className="glass rounded-3xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Target size={20} style={{color:"var(--primary)"}} />
          <h3 className="text-lg font-bold">{t("goals_title")}</h3>
        </div>
        <input className="input" placeholder={t("goal_title_input")}
          value={title} onChange={e=>setTitle(e.target.value)} required data-testid="goal-title-input"/>
        <textarea className="input" rows={3} placeholder={t("goal_desc_input")}
          value={desc} onChange={e=>setDesc(e.target.value)} data-testid="goal-desc-input"/>
        <button className="btn w-full" data-testid="goal-add-btn">
          <Plus size={16}/> {t("add_goal")}
        </button>
      </form>

      <div className="space-y-3" data-testid="goals-list">
        {goals.length === 0 && (
          <div className="glass rounded-3xl p-6 text-center text-stone-500">
            {t("no_goals")}
          </div>
        )}
        {goals.map(g => (
          <div key={g.id} className="glass rounded-2xl p-4 flex items-start gap-3 fade-up"
            data-testid={`goal-item-${g.id}`}>
            <button onClick={() => toggle(g)} data-testid={`goal-toggle-${g.id}`}
              className="mt-1 rounded-full w-6 h-6 flex items-center justify-center border-2"
              style={{
                borderColor: g.completed ? "var(--primary)" : "var(--border)",
                background: g.completed ? "var(--primary)" : "transparent",
                color: "white"
              }}>
              {g.completed && <Check size={14} />}
            </button>
            <div className="flex-1">
              <div className={"font-bold " + (g.completed ? "line-through text-stone-400" : "")}>
                {g.title}
              </div>
              {g.description && <div className="text-sm text-stone-600 mt-1">{g.description}</div>}
            </div>
            <button onClick={() => remove(g.id)} className="text-stone-400 hover:text-red-600"
              data-testid={`goal-delete-${g.id}`}>
              <Trash2 size={16}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Weekly Panel ----------
function WeeklyPanel() {
  const { authHeaders, lang } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = useT();

  const load = () => {
    setLoading(true);
    axios.get(`${API}/weekly-summary`, { headers: authHeaders })
      .then(({ data }) => setData(data))
      .catch(() => setData({ stats: {}, narrative: "" }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [authHeaders]);

  const s = data?.stats || {};
  const topMoodInfo = MOODS.find(m => m.key === (s.top_mood || "neutral")) || MOODS[1];
  const TopIcon = topMoodInfo.icon;

  return (
    <div className="space-y-6" data-testid="weekly-panel">
      <div className="glass rounded-3xl p-5 sm:p-7">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BarChart3 size={22} style={{ color: "var(--primary)" }} />
            <h3 className="text-xl font-extrabold">{t("weekly_title")}</h3>
          </div>
          <button className="btn-ghost btn" onClick={load} disabled={loading}
            data-testid="weekly-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t("weekly_refresh")}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatTile icon={TopIcon} color={topMoodInfo.color}
            label={t("weekly_top_mood")} value={moodLabel(lang, s.top_mood || "neutral")} />
          <StatTile icon={BookOpen} color="#D4A373"
            label={t("weekly_journal_count")} value={s.journal_entries ?? 0} />
          <StatTile icon={Check} color="#8FBC8F"
            label={t("weekly_goals_completed")} value={s.goals_completed ?? 0} />
          <StatTile icon={Target} color="#FF7F50"
            label={t("weekly_active_goals")} value={s.active_goals ?? 0} />
          <StatTile icon={MessageCircle} color="#B39DDB"
            label={t("weekly_chat_count")} value={s.chat_messages ?? 0} />
        </div>

        <div className="bubble-ai" data-testid="weekly-narrative">
          {loading
            ? <span className="text-stone-500">{t("weekly_loading")}</span>
            : (linkify(data?.narrative) || t("weekly_loading"))
          }
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, color, label, value }) {
  return (
    <div className="glass rounded-2xl p-3 text-center">
      <Icon size={20} style={{ color }} className="mx-auto mb-1" />
      <div className="text-xs text-stone-500">{label}</div>
      <div className="text-lg font-extrabold">{value}</div>
    </div>
  );
}

// ---------- Insights Panel ----------
function InsightsPanel() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = useT();

  const load = () => {
    setLoading(true);
    axios.get(`${API}/insights`, { headers: authHeaders })
      .then(({ data }) => setData(data))
      .catch(() => setData({ stats: {}, themes: [], narrative: "" }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="insights-panel">
      <div className="glass rounded-3xl p-5 sm:p-7">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={22} style={{ color: "var(--primary)" }} />
            <h3 className="text-xl font-extrabold">{t("insights_title")}</h3>
          </div>
          <button className="btn-ghost btn" onClick={load} disabled={loading}
            data-testid="insights-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {t("insights_refresh")}
          </button>
        </div>
        <p className="text-sm text-stone-600 mb-5">{t("insights_intro")}</p>

        {loading ? (
          <div className="dots text-center py-10"><span/><span/><span/></div>
        ) : (
          <>
            {data?.narrative && (
              <div className="bubble-ai mb-5" data-testid="insights-narrative">
                {linkify(data.narrative)}
              </div>
            )}
            {data?.themes && data.themes.length > 0 && (
              <div>
                <h4 className="font-bold mb-3 text-stone-700">{t("insights_themes")}</h4>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="insights-themes">
                  {data.themes.map((th, i) => (
                    <div key={i} className="rounded-2xl border border-stone-200 bg-white/70 p-4 fade-up"
                      data-testid={`insight-theme-${i}`}>
                      <div className="font-bold mb-1 flex items-center gap-2">
                        <Sparkles size={14} style={{ color: "var(--primary)" }} />
                        {th.title}
                      </div>
                      <div className="text-sm text-stone-600 leading-relaxed">{th.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!data?.narrative && (!data?.themes || data.themes.length === 0) && !loading && (
              <div className="text-center text-stone-500 py-6">
                {t("memories_empty")}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Memories Panel ----------
function MemoriesPanel() {
  const { authHeaders } = useAuth();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = useT();

  const load = () => {
    setLoading(true);
    axios.get(`${API}/memories`, { headers: authHeaders })
      .then(({ data }) => setMemories(data.memories || []))
      .catch(() => setMemories([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/memories/${id}`, { headers: authHeaders });
      load();
    } catch {}
  };

  const clearAll = async () => {
    if (!window.confirm(t("confirm_clear_memories"))) return;
    try {
      await axios.delete(`${API}/memories`, { headers: authHeaders });
      load();
    } catch {}
  };

  return (
    <div className="space-y-4" data-testid="memories-panel">
      <div className="glass rounded-3xl p-5 sm:p-7">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain size={22} style={{ color: "var(--primary)" }} />
            <h3 className="text-xl font-extrabold">{t("memories_title")}</h3>
          </div>
          {memories.length > 0 && (
            <button className="btn-ghost btn text-sm" onClick={clearAll}
              data-testid="memories-clear-all-btn">
              <Trash2 size={14} /> {t("clear_all_memories")}
            </button>
          )}
        </div>
        <p className="text-sm text-stone-600 mb-5">{t("memories_intro")}</p>

        {loading ? (
          <div className="dots text-center py-10"><span/><span/><span/></div>
        ) : memories.length === 0 ? (
          <div className="text-center text-stone-500 py-10">{t("memories_empty")}</div>
        ) : (
          <ul className="space-y-2" data-testid="memories-list">
            {memories.map(m => (
              <li key={m.id}
                className="flex items-start gap-3 bg-white/60 border border-stone-200 rounded-2xl p-3 fade-up"
                data-testid={`memory-${m.id}`}>
                <Sparkles size={16} style={{ color: "var(--primary)" }} className="mt-1 shrink-0" />
                <span className="flex-1 text-stone-700 leading-relaxed">{m.text}</span>
                <button onClick={() => remove(m.id)} className="text-stone-400 hover:text-red-600"
                  data-testid={`memory-delete-${m.id}`}>
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Protected Wrapper ----------
function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <ThemeManager age={25} mood="neutral" />
        <div className="mood-bg"/>
        <div className="dots"><span/><span/><span/></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AnonOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

// ---------- Auth Callback (Google + Facebook) ----------
function AuthCallback() {
  const { saveAuth } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const [err, setErr] = useState("");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = (window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const sessionId = params.get("session_id");
    const tokenFromFb = params.get("token");
    const errorCode = params.get("error");

    if (errorCode) {
      setErr(t("auth_social_error"));
      setTimeout(() => nav("/login", { replace: true }), 1800);
      return;
    }

    // Clean the URL so refresh doesn't re-process.
    window.history.replaceState({}, document.title, window.location.pathname);

    const finish = async () => {
      try {
        if (sessionId) {
          const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
          saveAuth(data.user, data.token);
          nav("/app", { replace: true });
          return;
        }
        if (tokenFromFb) {
          const { data } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${tokenFromFb}` },
          });
          saveAuth(data.user, tokenFromFb);
          nav("/app", { replace: true });
          return;
        }
        // No fragment — go home.
        nav("/login", { replace: true });
      } catch (e) {
        setErr(formatError(e?.response?.data?.detail) || t("auth_social_error"));
        setTimeout(() => nav("/login", { replace: true }), 2000);
      }
    };
    finish();
  }, [nav, saveAuth, t]);

  return (
    <div className="grain min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <ThemeManager age={25} mood="neutral" />
      <div className="mood-bg" />
      <div className="glass rounded-3xl p-8 text-center max-w-sm">
        <div className="dots mx-auto mb-4"><span/><span/><span/></div>
        <div className="text-lg font-bold">{err || t("auth_finalizing")}</div>
        {!err && <div className="text-xs text-stone-500 mt-2">{t("auth_one_moment")}</div>}
      </div>
    </div>
  );
}

// ---------- Images Panel (Gemini Nano Banana) ----------
function ImagesPanel() {
  const { authHeaders } = useAuth();
  const t = useT();
  const [prompt, setPrompt] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);
  const promptRef = useRef(null);

  // Ready-made prompt suggestions (50+) with real generated preview images.
  // See /app/frontend/src/imageSuggestions.js
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const visibleSuggestions = showAllSuggestions ? IMG_SUGGESTIONS : FEATURED_SUGGESTIONS;

  const applyPromptSuggestion = (text) => {
    setPrompt(text);
    setError("");
    setResult(null);
    // focus the textarea so user can edit
    setTimeout(() => {
      promptRef.current?.focus();
      promptRef.current?.setSelectionRange(text.length, text.length);
      promptRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const loadHistory = () => {
    axios.get(`${API}/image/history`, { headers: authHeaders })
      .then(({ data }) => setHistory(data.items || []))
      .catch(() => {});
  };
  useEffect(() => { loadHistory(); }, []);

  const onPickFile = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      setError(t("img_too_big"));
      return;
    }
    setFile(f);
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!prompt.trim()) { setError(t("img_prompt_required")); return; }
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt.trim());
      let url = `${API}/image/generate`;
      if (file) {
        fd.append("image", file);
        url = `${API}/image/edit`;
      }
      const { data } = await axios.post(url, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || t("error_retry"));
    } finally { setBusy(false); }
  };

  const reset = () => {
    setPrompt(""); setFile(null); setPreview(null); setResult(null); setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeItem = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await axios.delete(`${API}/image/${id}`, { headers: authHeaders });
      setHistory(h => h.filter(x => x.id !== id));
    } catch {}
  };

  return (
    <div className="space-y-4" data-testid="images-panel">
      <div className="glass rounded-3xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Wand2 size={20} style={{ color: "var(--primary)" }} />
          <div className="text-lg font-bold">{t("images_title")}</div>
        </div>
        <div className="text-xs text-stone-400 mb-4">
          {file
            ? "اختر اقتراحاً جاهزاً أو اكتب وصفك — وسأعدّل صورتك"
            : "اختر اقتراحاً جاهزاً أو اكتب وصفك لإنشاء صورة جديدة. ارفع صورة لتعديلها."}
        </div>

        {/* Optional image upload (unified UI) */}
        <div className="mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <input type="file" accept="image/*" ref={fileRef} className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="img-file-input" />
            {!preview ? (
              <button type="button"
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-dashed transition hover:scale-[1.01]"
                style={{ borderColor: "rgba(255, 215, 130, 0.35)", background: "rgba(255, 215, 130, 0.05)", color: "var(--primary)" }}
                data-testid="img-upload-btn">
                <Upload size={16} />
                <span className="font-semibold">{t("img_choose_file") || "ارفع صورة لتعديلها (اختياري)"}</span>
              </button>
            ) : (
              <div className="flex items-center gap-3 rounded-2xl p-2 pr-4"
                   style={{ background: "rgba(255, 215, 130, 0.08)", border: "1px solid rgba(255, 215, 130, 0.25)" }}>
                <div className="relative shrink-0">
                  <img src={preview} alt="preview"
                    className="h-16 w-16 object-cover rounded-xl border"
                    style={{ borderColor: "rgba(255, 215, 130, 0.4)" }} />
                  <button onClick={() => { setFile(null); setPreview(null); if (fileRef.current) fileRef.current.value=""; }}
                    className="absolute -top-2 -end-2 bg-red-500 text-white rounded-full p-1 shadow"
                    data-testid="img-clear-file" aria-label="remove">
                    <X size={12} />
                  </button>
                </div>
                <div className="text-xs">
                  <div className="font-bold text-amber-300 mb-0.5">صورتك جاهزة للتعديل</div>
                  <div className="text-stone-400 line-clamp-1 max-w-[200px]">{file?.name}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Suggestions grid with image-like gradient previews */}
        <div className="mb-5" data-testid="img-suggestions">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold flex items-center gap-1.5">
              <Sparkles size={14} style={{ color: "var(--primary)" }} />
              <span>أفكار جاهزة</span>
            </div>
            <div className="text-[11px] text-stone-400">
              {file ? "سيُطبَّق الاقتراح على صورتك" : "اختر فكرة لتعبئة الوصف"}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {visibleSuggestions.map(s => (
              <button key={s.key} type="button"
                onClick={() => applyPromptSuggestion(s.prompt)}
                className="group relative rounded-2xl overflow-hidden text-start transition hover:scale-[1.03] hover:shadow-xl"
                style={{ border: "1px solid rgba(255, 215, 130, 0.18)" }}
                data-testid={`img-suggestion-${s.key}`}
                title={s.prompt}>
                {/* Real generated preview image */}
                <div className="relative aspect-[4/3] overflow-hidden"
                     style={{ background: "#1a1410" }}>
                  <img src={`/img-suggestions/${s.key}.png`}
                       alt={s.title}
                       loading="lazy"
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                       onError={(e) => { e.currentTarget.style.opacity = "0"; }}/>
                  {/* Soft top-right vignette for readability */}
                  <div className="absolute inset-0 pointer-events-none"
                       style={{ background: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.35) 100%)" }}/>
                </div>
                {/* Caption */}
                <div className="p-2.5"
                     style={{ background: "rgba(26, 20, 16, 0.92)" }}>
                  <div className="text-sm font-bold text-amber-200 line-clamp-1">{s.title}</div>
                  {s.subtitle && (
                    <div className="text-[10.5px] text-stone-400 line-clamp-1 mt-0.5">{s.subtitle}</div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* "Show more / less" toggle */}
          {IMG_SUGGESTIONS.length > FEATURED_SUGGESTIONS.length && (
            <div className="flex justify-center mt-4">
              <button type="button"
                onClick={() => setShowAllSuggestions(s => !s)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition hover:scale-[1.03]"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 215, 130, 0.18), rgba(255, 215, 130, 0.08))",
                  border: "1px solid rgba(255, 215, 130, 0.35)",
                  color: "var(--primary)",
                }}
                data-testid="img-suggestions-toggle">
                {showAllSuggestions ? (
                  <>
                    <ChevronDown size={16} className="rotate-180"/>
                    <span>عرض أقل</span>
                  </>
                ) : (
                  <>
                    <span>عرض المزيد</span>
                    <span className="text-[11px] opacity-70">
                      ({IMG_SUGGESTIONS.length - FEATURED_SUGGESTIONS.length}+ فكرة)
                    </span>
                    <ChevronDown size={16}/>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <label className="block text-sm font-bold mb-2">{t("img_prompt_label")}</label>
        <textarea ref={promptRef} value={prompt} onChange={(e) => setPrompt(e.target.value)}
          rows={4} className="input w-full"
          placeholder={file ? "صف التعديل الذي تريده على صورتك..." : (t("img_prompt_gen_ph") || "صف الصورة التي تريد إنشاءها...")}
          data-testid="img-prompt-input" />

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button className="btn-primary flex items-center gap-2" disabled={busy} onClick={submit}
            data-testid="img-submit-btn">
            {busy ? <RefreshCw size={16} className="spin" /> : <Wand2 size={16} />}
            {busy
              ? (t("img_working") || "جاري العمل...")
              : (file ? (t("img_btn_edit") || "عدّل صورتي") : (t("img_btn_generate") || "أنشئ الصورة"))}
          </button>
          <button className="btn-secondary" onClick={reset} disabled={busy} data-testid="img-reset-btn">
            {t("clear")}
          </button>
        </div>
        {error && <div className="mt-3 text-sm text-red-600" data-testid="img-error">{error}</div>}

        {result && (
          <div className="mt-5 p-3 bg-stone-50 rounded-2xl border border-stone-200" data-testid="img-result">
            <img src={result.data_url} alt="result" className="max-h-[420px] rounded-xl mx-auto" />
            <div className="flex items-center justify-between mt-3">
              <div className="text-xs text-stone-500 line-clamp-1">{result.prompt}</div>
              <a href={result.data_url} download={`hayatak-${result.id}.png`}
                className="text-sm flex items-center gap-1 text-amber-700 hover:underline"
                data-testid="img-download-btn">
                <Download size={14} /> {t("download")}
              </a>
            </div>
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="glass rounded-3xl p-5" data-testid="img-history">
          <div className="text-sm font-bold mb-3 flex items-center gap-2">
            <ImageIcon size={16} /> {t("img_history")}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {history.map(it => (
              <div key={it.id} className="relative group rounded-xl overflow-hidden border border-stone-200 bg-white">
                <img src={it.data_url} alt={it.prompt} className="aspect-square object-cover w-full" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <div className="text-[10px] text-white line-clamp-2">{it.prompt}</div>
                </div>
                <button onClick={() => removeItem(it.id)}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                  aria-label="delete" data-testid={`img-del-${it.id}`}>
                  <Trash2 size={12} className="text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- PDF Panel (OpenAI analysis) ----------
function PdfPanel() {
  const { authHeaders, user, lang } = useAuth();
  const t = useT();
  const [file, setFile] = useState(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const loadHistory = () => {
    axios.get(`${API}/pdf/history`, { headers: authHeaders })
      .then(({ data }) => setHistory(data.items || []))
      .catch(() => {});
  };
  useEffect(() => { loadHistory(); }, []);

  const onPickFile = (f) => {
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { setError(t("pdf_too_big")); return; }
    setFile(f); setError("");
  };

  const submit = async () => {
    if (!file) { setError(t("pdf_file_required")); return; }
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      fd.append("question", question.trim());
      fd.append("language", lang || user?.language || "ar");
      const { data } = await axios.post(`${API}/pdf/analyze`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || t("error_retry"));
    } finally { setBusy(false); }
  };

  const reset = () => {
    setFile(null); setQuestion(""); setResult(null); setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeItem = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await axios.delete(`${API}/pdf/${id}`, { headers: authHeaders });
      setHistory(h => h.filter(x => x.id !== id));
    } catch {}
  };

  return (
    <div className="space-y-4" data-testid="pdf-panel">
      <div className="glass rounded-3xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={20} style={{ color: "var(--primary)" }} />
          <div className="text-lg font-bold">{t("pdf_title")}</div>
          <span className="text-xs text-stone-500">{t("pdf_subtitle")}</span>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-bold mb-2">{t("pdf_upload_label")}</label>
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" className="btn-secondary flex items-center gap-2"
              onClick={() => fileRef.current?.click()} data-testid="pdf-upload-btn">
              <Upload size={16} /> {t("pdf_choose_file")}
            </button>
            <input type="file" accept="application/pdf,.pdf" ref={fileRef} className="hidden"
              onChange={(e) => onPickFile(e.target.files?.[0])} data-testid="pdf-file-input" />
            {file && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
                <FileText size={14} />
                <span className="text-sm">{file.name}</span>
                <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value=""; }}
                  data-testid="pdf-clear-file" aria-label="remove"><X size={12} /></button>
              </div>
            )}
          </div>
        </div>

        <label className="block text-sm font-bold mb-2">{t("pdf_question_label")}</label>
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={3}
          className="input w-full" placeholder={t("pdf_question_ph")}
          data-testid="pdf-question-input" />

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button className="btn-primary flex items-center gap-2" disabled={busy} onClick={submit}
            data-testid="pdf-submit-btn">
            {busy ? <RefreshCw size={16} className="spin" /> : <Brain size={16} />}
            {busy ? t("pdf_working") : t("pdf_analyze_btn")}
          </button>
          <button className="btn-secondary" onClick={reset} disabled={busy} data-testid="pdf-reset-btn">
            {t("clear")}
          </button>
        </div>
        {error && <div className="mt-3 text-sm text-red-600" data-testid="pdf-error">{error}</div>}

        {result && (
          <div className="mt-5 p-4 bg-stone-50 rounded-2xl border border-stone-200" data-testid="pdf-result">
            <div className="text-xs text-stone-500 mb-1">{result.filename} • {result.pages} {t("pdf_pages")}</div>
            {result.truncated && <div className="text-xs text-amber-700 mb-2">{t("pdf_truncated")}</div>}
            <div className="text-xs font-bold text-stone-600 mb-1">{t("pdf_question_label")}:</div>
            <div className="text-sm mb-3 text-stone-700">{result.question}</div>
            <div className="text-xs font-bold text-stone-600 mb-1">{t("pdf_answer_label")}:</div>
            <div className="text-stone-800 whitespace-pre-wrap leading-relaxed">{linkify(result.answer)}</div>
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="glass rounded-3xl p-5" data-testid="pdf-history">
          <div className="text-sm font-bold mb-3 flex items-center gap-2">
            <FileText size={16} /> {t("pdf_history")}
          </div>
          <div className="space-y-3">
            {history.map(it => (
              <details key={it.id} className="bg-white rounded-2xl border border-stone-200 p-3">
                <summary className="cursor-pointer flex items-center justify-between gap-2">
                  <div className="flex-1">
                    <div className="text-sm font-bold line-clamp-1">{it.filename}</div>
                    <div className="text-xs text-stone-500 line-clamp-1">{it.question}</div>
                  </div>
                  <button onClick={(e) => { e.preventDefault(); removeItem(it.id); }}
                    aria-label="delete" data-testid={`pdf-del-${it.id}`}>
                    <Trash2 size={14} className="text-red-600" />
                  </button>
                </summary>
                <div className="mt-3 text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
                  {linkify(it.answer)}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Appointments Reminder Banner (shown app-wide) ----------
function AppointmentReminderBanner() {
  const { user, authHeaders } = useAuth();
  const [due, setDue] = useState([]);

  const formatOffset = (m) => {
    if (m >= 10080) return `قبل ${Math.round(m/10080)} أسبوع`;
    if (m >= 1440)  return `قبل ${Math.round(m/1440)} يوم`;
    if (m >= 60)    return `قبل ${Math.round(m/60)} ساعة`;
    return `قبل ${m} دقيقة`;
  };
  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleString("ar-EG", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const fireBrowserNotif = (item) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const key = `notif_shown_${item.id}_${item.trigger_offset}`;
    if (localStorage.getItem(key)) return;
    try {
      const n = new Notification(`📅 ${item.title}`, {
        body: `${formatOffset(item.trigger_offset)} · ${formatTime(item.when)}${item.location ? " · " + item.location : ""}`,
        icon: "/icon-192.png",
        tag: `appt-${item.id}-${item.trigger_offset}`,
        requireInteraction: item.importance === "high",
      });
      n.onclick = () => { window.focus(); };
      localStorage.setItem(key, "1");
    } catch {}
  };

  const poll = async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/appointments/upcoming`, { headers: authHeaders });
      setDue(data || []);
      (data || []).forEach(fireBrowserNotif);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    poll();
    const id = setInterval(poll, 60000); // every 60s
    return () => clearInterval(id);
  }, [user]);

  const dismiss = async (item) => {
    try {
      await axios.post(`${API}/appointments/${item.id}/ack?offset=${item.trigger_offset}`, {}, { headers: authHeaders });
      setDue(prev => prev.filter(x => x.id !== item.id));
    } catch {}
  };

  if (!due.length) return null;
  return (
    <div className="space-y-2 mb-4" data-testid="reminder-banner">
      {due.map(item => {
        const isHigh = item.importance === "high";
        const bg = isHigh ? "rgba(220,38,38,0.08)" : "rgba(245,158,11,0.10)";
        const border = isHigh ? "rgba(220,38,38,0.35)" : "rgba(245,158,11,0.35)";
        const color = isHigh ? "#dc2626" : "#b45309";
        return (
          <div key={`${item.id}-${item.trigger_offset}`} className="rounded-2xl p-3 flex items-start gap-3 flex-wrap"
               style={{ background: bg, border: `1px solid ${border}` }} data-testid="reminder-item">
            <div className="shrink-0 mt-0.5"><Bell size={20} style={{ color }} /></div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-bold text-stone-800 flex items-center gap-2 flex-wrap">
                <span>{item.title}</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: color, color: "#fff" }}>
                  {formatOffset(item.trigger_offset)}
                </span>
              </div>
              <div className="text-xs text-stone-600 mt-0.5 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><Clock size={12}/>{formatTime(item.when)}</span>
                {item.location && <span className="flex items-center gap-1"><MapPin size={12}/>{item.location}</span>}
              </div>
              {item.description && <div className="text-sm text-stone-700 mt-1">{item.description}</div>}
            </div>
            <button className="btn-ghost btn text-xs shrink-0" onClick={() => dismiss(item)} data-testid="reminder-dismiss-btn">
              <Check size={14}/> فهمت
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Appointments Panel ----------
const APPT_COLORS = ["#C19A6B", "#dc2626", "#16a34a", "#0ea5e9", "#a855f7", "#f59e0b"];
const REMINDER_OPTS = [
  { v: 15,    label: "قبل 15 دقيقة" },
  { v: 60,    label: "قبل ساعة" },
  { v: 180,   label: "قبل 3 ساعات" },
  { v: 1440,  label: "قبل يوم" },
  { v: 10080, label: "قبل أسبوع" },
];

function AppointmentsPanel() {
  const { user, authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [scope, setScope] = useState("upcoming");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [permState, setPermState] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const blank = {
    title: "", description: "", date: "", time: "",
    location: "", importance: "normal", color: APPT_COLORS[0],
    reminders: [15, 60, 1440, 10080],
  };
  const [form, setForm] = useState(blank);

  const load = async () => {
    if (!user) { setBusy(false); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/appointments`, { headers: authHeaders, params: { scope } });
      setItems(data || []);
    } catch (e) {
      setErr("تعذّر تحميل المواعيد");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, user]);

  const requestNotifPerm = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setPermState(p);
    } catch {}
  };

  const openNew = () => { setEditing(null); setForm(blank); setShowForm(true); };
  const openEdit = (item) => {
    const dt = new Date(item.when);
    const pad = (n) => String(n).padStart(2, "0");
    const date = `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
    const time = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    setEditing(item);
    setForm({
      title: item.title || "",
      description: item.description || "",
      date, time,
      location: item.location || "",
      importance: item.importance || "normal",
      color: item.color || APPT_COLORS[0],
      reminders: item.reminders || [15, 60, 1440, 10080],
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!form.title.trim() || !form.date || !form.time) {
      setErr("يرجى تعبئة العنوان والتاريخ والوقت");
      return;
    }
    const whenLocal = new Date(`${form.date}T${form.time}`);
    if (isNaN(whenLocal.getTime())) { setErr("التاريخ غير صالح"); return; }
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      when: whenLocal.toISOString(),
      location: form.location.trim(),
      importance: form.importance,
      color: form.color,
      reminders: form.reminders,
    };
    try {
      if (editing) {
        await axios.put(`${API}/appointments/${editing.id}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/appointments`, payload, { headers: authHeaders });
      }
      setShowForm(false); setEditing(null); setForm(blank);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "تعذّر الحفظ");
    }
  };

  const del = async (item) => {
    if (!window.confirm(`حذف "${item.title}"؟`)) return;
    try { await axios.delete(`${API}/appointments/${item.id}`, { headers: authHeaders }); await load(); }
    catch { setErr("تعذّر الحذف"); }
  };
  const toggleDone = async (item) => {
    try { await axios.put(`${API}/appointments/${item.id}`, { done: !item.done }, { headers: authHeaders }); await load(); }
    catch {}
  };

  const toggleReminder = (v) => {
    setForm(prev => ({
      ...prev,
      reminders: prev.reminders.includes(v) ? prev.reminders.filter(x => x !== v) : [...prev.reminders, v],
    }));
  };

  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString("ar-EG", { dateStyle: "full", timeStyle: "short" }); }
    catch { return iso; }
  };
  const relWhen = (iso) => {
    const diffMin = Math.round((new Date(iso) - Date.now()) / 60000);
    if (diffMin < 0) return "انتهى";
    if (diffMin < 60) return `بعد ${diffMin} دقيقة`;
    if (diffMin < 1440) return `بعد ${Math.round(diffMin/60)} ساعة`;
    if (diffMin < 10080) return `بعد ${Math.round(diffMin/1440)} يوم`;
    return `بعد ${Math.round(diffMin/10080)} أسبوع`;
  };
  const importanceBadge = (imp) => {
    const map = { high: { lbl: "مهم جداً", bg: "#dc2626" }, medium: { lbl: "متوسط", bg: "#f59e0b" }, normal: { lbl: "عادي", bg: "#78716c" } };
    const m = map[imp] || map.normal;
    return <span className="text-[10px] px-2 py-0.5 rounded-full text-white font-bold" style={{ background: m.bg }}>{m.lbl}</span>;
  };

  return (
    <div className="space-y-4" data-testid="appointments-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar size={20} style={{ color: "var(--primary)" }} />
          <h2 className="font-black text-xl">جدول مواعيدي</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {permState === "default" && (
            <button className="btn-ghost btn text-sm" onClick={requestNotifPerm} data-testid="enable-notif-btn">
              <Bell size={14}/> تفعيل الإشعارات
            </button>
          )}
          {permState === "granted" && <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1"><Bell size={12}/> الإشعارات مفعّلة</span>}
          <button className="btn" onClick={openNew} data-testid="appt-new-btn"><Plus size={16}/> موعد جديد</button>
        </div>
      </div>

      {err && <div className="glass rounded-2xl p-3 text-red-700 text-sm" data-testid="appt-error">{err}</div>}

      {/* Scope tabs */}
      <div className="glass rounded-full p-1.5 inline-flex gap-1" data-testid="appt-scope">
        {[
          { k: "upcoming", lbl: "قادمة" },
          { k: "past",     lbl: "ماضية" },
          { k: "all",      lbl: "الكل" },
        ].map(s => (
          <button key={s.k} className="tab" data-active={scope === s.k} onClick={() => setScope(s.k)} data-testid={`appt-scope-${s.k}`}>
            {s.lbl}
          </button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <form className="glass rounded-3xl p-5 space-y-3" onSubmit={save} data-testid="appt-form">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">{editing ? "تعديل الموعد" : "موعد جديد"}</h3>
            <button type="button" className="btn-ghost btn text-xs" onClick={() => { setShowForm(false); setEditing(null); }}><X size={14}/></button>
          </div>
          <label className="block">
            <span className="text-sm text-stone-600">عنوان الموعد</span>
            <input className="input w-full mt-1" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="مثال: زيارة الطبيب" required data-testid="appt-title-input"/>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm text-stone-600">التاريخ</span>
              <input type="date" className="input w-full mt-1" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required data-testid="appt-date-input"/>
            </label>
            <label className="block">
              <span className="text-sm text-stone-600">الوقت</span>
              <input type="time" className="input w-full mt-1" value={form.time} onChange={e => setForm({...form, time: e.target.value})} required data-testid="appt-time-input"/>
            </label>
          </div>
          <label className="block">
            <span className="text-sm text-stone-600">ملخص / تفاصيل</span>
            <textarea className="input w-full mt-1" rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="ما الذي يدور حوله هذا الموعد؟" data-testid="appt-desc-input"/>
          </label>
          <label className="block">
            <span className="text-sm text-stone-600">المكان (اختياري)</span>
            <input className="input w-full mt-1" value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="مثال: مستشفى الكندي" data-testid="appt-location-input"/>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm text-stone-600">الأهمية</span>
              <select className="input w-full mt-1" value={form.importance} onChange={e => setForm({...form, importance: e.target.value})} data-testid="appt-importance-select">
                <option value="high">مهم جداً</option>
                <option value="medium">متوسط</option>
                <option value="normal">عادي</option>
              </select>
            </label>
            <div>
              <span className="text-sm text-stone-600">اللون</span>
              <div className="flex gap-2 mt-1 flex-wrap">
                {APPT_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm({...form, color: c})}
                    className="w-9 h-9 rounded-full transition-transform"
                    style={{ background: c, boxShadow: form.color === c ? `0 0 0 3px white, 0 0 0 5px ${c}` : "none", transform: form.color === c ? "scale(1.1)" : "scale(1)" }}
                    aria-label={c} />
                ))}
              </div>
            </div>
          </div>
          <div>
            <span className="text-sm text-stone-600">متى تحب أذكّرك؟</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {REMINDER_OPTS.map(o => {
                const active = form.reminders.includes(o.v);
                return (
                  <button type="button" key={o.v} onClick={() => toggleReminder(o.v)}
                    className="px-3 py-1.5 rounded-full text-xs font-bold border transition-colors"
                    style={{ background: active ? "var(--primary)" : "rgba(255,255,255,0.6)", color: active ? "white" : "#57534e", borderColor: active ? "var(--primary)" : "#e7e5e4" }}
                    data-testid={`appt-reminder-${o.v}`}>
                    {active && "✓ "}{o.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn" data-testid="appt-save-btn"><Check size={16}/> {editing ? "حفظ التعديلات" : "إنشاء الموعد"}</button>
          </div>
        </form>
      )}

      {/* List */}
      {busy ? (
        <div className="glass rounded-3xl p-8 text-center"><div className="dots mx-auto mb-2"><span/><span/><span/></div><div className="text-stone-500">جارٍ التحميل…</div></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-3xl p-8 text-center text-stone-500" data-testid="appt-empty">
          <Calendar size={32} className="mx-auto mb-2 opacity-50"/>
          {scope === "upcoming" ? "لا توجد مواعيد قادمة." : scope === "past" ? "لا توجد مواعيد ماضية." : "لم تضف أي موعد بعد."}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <div key={item.id} className="glass rounded-3xl p-4 flex gap-3 items-start" data-testid="appt-card" style={{ opacity: item.done ? 0.55 : 1 }}>
              <div className="w-1.5 self-stretch rounded-full" style={{ background: item.color || "#C19A6B" }}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className={"font-bold text-lg " + (item.done ? "line-through text-stone-400" : "")}>{item.title}</h4>
                  {importanceBadge(item.importance)}
                  {!item.done && new Date(item.when) > new Date() && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold">{relWhen(item.when)}</span>}
                </div>
                <div className="text-sm text-stone-600 mt-1 flex flex-wrap items-center gap-3">
                  <span className="flex items-center gap-1"><Clock size={13}/>{fmtWhen(item.when)}</span>
                  {item.location && <span className="flex items-center gap-1"><MapPin size={13}/>{item.location}</span>}
                </div>
                {item.description && <p className="text-sm text-stone-700 mt-2 leading-relaxed">{item.description}</p>}
                {(item.reminders || []).length > 0 && (
                  <div className="text-[10px] text-stone-500 mt-2 flex items-center gap-1 flex-wrap">
                    <Bell size={11}/>
                    تذكير: {item.reminders.map(r => (REMINDER_OPTS.find(o=>o.v===r)?.label || `${r}د`).replace("قبل ","")).join(" • ")}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button className="btn-ghost btn text-xs" onClick={() => toggleDone(item)} title={item.done ? "تراجع" : "تم"} data-testid="appt-done-btn">
                  <Check size={14}/>
                </button>
                <button className="btn-ghost btn text-xs" onClick={() => openEdit(item)} title="تعديل" data-testid="appt-edit-btn">
                  <RefreshCw size={14}/>
                </button>
                <button className="btn-ghost btn text-xs" onClick={() => del(item)} title="حذف" data-testid="appt-delete-btn">
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Health Panel ("صحتي") ----------
function HealthPanel() {
  const { authHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [busy, setBusy] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);

  // form state
  const [form, setForm] = useState({
    height_cm: "", weight_kg: "", age: "", gender: "other",
    activity_level: "moderate", goal: "maintain",
    diseases: "", allergies: "", medications: "",
  });
  const [newWeight, setNewWeight] = useState("");
  const [weightNote, setWeightNote] = useState("");

  const loadAll = async () => {
    setBusy(true);
    try {
      const [p, a, c] = await Promise.all([
        axios.get(`${API}/health/profile`, { headers: authHeaders }),
        axios.get(`${API}/health/analysis`, { headers: authHeaders }),
        axios.get(`${API}/health/checkins`, { headers: authHeaders, params: { days: 90 } }),
      ]);
      const prof = p.data && p.data.user_id ? p.data : null;
      setProfile(prof);
      setAnalysis(a.data && a.data.summary ? a.data : null);
      setCheckins(c.data || []);
      if (!prof) setShowForm(true);
    } catch (e) {
      setErr("تعذّر تحميل البيانات الصحية");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveProfile = async (e) => {
    e?.preventDefault();
    setErr("");
    const payload = {
      height_cm: Number(form.height_cm),
      weight_kg: Number(form.weight_kg),
      age: Number(form.age),
      gender: form.gender,
      activity_level: form.activity_level,
      goal: form.goal,
      diseases: form.diseases.split(",").map(s => s.trim()).filter(Boolean),
      allergies: form.allergies.split(",").map(s => s.trim()).filter(Boolean),
      medications: form.medications.split(",").map(s => s.trim()).filter(Boolean),
    };
    if (!payload.height_cm || !payload.weight_kg || !payload.age) {
      setErr("يرجى تعبئة الطول والوزن والعمر");
      return;
    }
    try {
      await axios.post(`${API}/health/profile`, payload, { headers: authHeaders });
      setShowForm(false);
      await loadAll();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "خطأ في الحفظ");
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/health/analyze`, {}, { headers: authHeaders, timeout: 90000 });
      setAnalysis(data);
    } catch (e) {
      // Gateway 502/504 or timeout: backend keeps running and persists the result.
      // Poll /health/analysis up to ~90s before declaring failure.
      const status = e?.response?.status;
      const isTimeout = e?.code === "ECONNABORTED" || status === 502 || status === 504 || !status;
      if (isTimeout) {
        const before = analysis?.created_at || null;
        const deadline = Date.now() + 90000;
        let recovered = null;
        while (Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 5000));
          try {
            const { data: latest } = await axios.get(`${API}/health/analysis`, { headers: authHeaders });
            if (latest && latest.summary && latest.created_at !== before) {
              recovered = latest;
              break;
            }
          } catch {}
        }
        if (recovered) {
          setAnalysis(recovered);
          setAnalyzing(false);
          return;
        }
      }
      setErr(e?.response?.data?.detail || "تعذّر توليد التحليل، حاول مرة أخرى");
    } finally {
      setAnalyzing(false);
    }
  };

  const addCheckin = async () => {
    const w = Number(newWeight);
    if (!w || w < 20 || w > 400) { setErr("الرجاء إدخال وزن صحيح"); return; }
    setErr("");
    try {
      await axios.post(`${API}/health/checkin`, { weight_kg: w, note: weightNote }, { headers: authHeaders });
      setNewWeight(""); setWeightNote("");
      await loadAll();
    } catch (e) {
      setErr("تعذّر تسجيل القياس");
    }
  };

  const editProfile = () => {
    if (profile) {
      setForm({
        height_cm: profile.height_cm || "",
        weight_kg: profile.weight_kg || "",
        age: profile.age || "",
        gender: profile.gender || "other",
        activity_level: profile.activity_level || "moderate",
        goal: profile.goal || "maintain",
        diseases: (profile.diseases || []).join(", "),
        allergies: (profile.allergies || []).join(", "),
        medications: (profile.medications || []).join(", "),
      });
    }
    setShowForm(true);
  };

  if (busy) {
    return <div className="glass rounded-3xl p-8 text-center"><div className="dots mx-auto mb-3"><span/><span/><span/></div><div className="text-stone-600">جارٍ التحميل…</div></div>;
  }

  return (
    <div className="space-y-5" data-testid="health-panel">
      {err && <div className="glass rounded-2xl p-3 text-red-700 text-sm" data-testid="health-error">{err}</div>}

      {/* Medical disclaimer */}
      <div className="rounded-2xl p-4 flex gap-3 items-start" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
        <AlertCircle size={20} className="shrink-0 mt-0.5" style={{ color: "#b45309" }} />
        <div className="text-sm text-stone-700 leading-relaxed">
          <b>تنبيه:</b> هذه نصائح إرشادية عامة من الذكاء الاصطناعي ولا تُغني عن استشارة الطبيب المختص. إذا كنت تعاني من حالة مزمنة، حامل، أو تتناول أدوية، استشر طبيبك قبل تطبيق أي توصية.
        </div>
      </div>

      {/* Profile form / display */}
      {showForm ? (
        <form className="glass rounded-3xl p-5 space-y-4" onSubmit={saveProfile} data-testid="health-form">
          <div className="flex items-center gap-2">
            <HeartPulse size={20} style={{ color: "var(--primary)" }} />
            <h3 className="font-bold text-lg">بياناتك الصحية</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-sm text-stone-600">الطول (سم)</span>
              <input type="number" min="50" max="260" className="input w-full mt-1" value={form.height_cm} onChange={e => setForm({...form, height_cm: e.target.value})} required data-testid="health-height-input" />
            </label>
            <label className="block">
              <span className="text-sm text-stone-600">الوزن الحالي (كغ)</span>
              <input type="number" min="20" max="400" step="0.1" className="input w-full mt-1" value={form.weight_kg} onChange={e => setForm({...form, weight_kg: e.target.value})} required data-testid="health-weight-input" />
            </label>
            <label className="block">
              <span className="text-sm text-stone-600">العمر</span>
              <input type="number" min="10" max="120" className="input w-full mt-1" value={form.age} onChange={e => setForm({...form, age: e.target.value})} required data-testid="health-age-input" />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-sm text-stone-600">الجنس</span>
              <select className="input w-full mt-1" value={form.gender} onChange={e => setForm({...form, gender: e.target.value})} data-testid="health-gender-select">
                <option value="male">ذكر</option>
                <option value="female">أنثى</option>
                <option value="other">آخر</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-stone-600">مستوى النشاط</span>
              <select className="input w-full mt-1" value={form.activity_level} onChange={e => setForm({...form, activity_level: e.target.value})} data-testid="health-activity-select">
                <option value="sedentary">قليل (مكتب)</option>
                <option value="light">خفيف (مشي قليل)</option>
                <option value="moderate">معتدل</option>
                <option value="active">نشط</option>
                <option value="very_active">نشط جداً (رياضي)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-stone-600">الهدف</span>
              <select className="input w-full mt-1" value={form.goal} onChange={e => setForm({...form, goal: e.target.value})} data-testid="health-goal-select">
                <option value="lose">إنقاص الوزن</option>
                <option value="gain">زيادة الوزن</option>
                <option value="maintain">الحفاظ على الوزن</option>
                <option value="fitness">لياقة وعضلات</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-sm text-stone-600">أمراض أو حالات صحية (افصل بفواصل، أو اتركه فارغاً)</span>
            <input type="text" placeholder="مثال: سكري، ضغط الدم" className="input w-full mt-1" value={form.diseases} onChange={e => setForm({...form, diseases: e.target.value})} data-testid="health-diseases-input" />
          </label>
          <label className="block">
            <span className="text-sm text-stone-600">حساسيات (طعام أو غيره)</span>
            <input type="text" placeholder="مثال: الفول السوداني، لاكتوز" className="input w-full mt-1" value={form.allergies} onChange={e => setForm({...form, allergies: e.target.value})} data-testid="health-allergies-input" />
          </label>
          <label className="block">
            <span className="text-sm text-stone-600">أدوية تتناولها</span>
            <input type="text" placeholder="مثال: ميتفورمين" className="input w-full mt-1" value={form.medications} onChange={e => setForm({...form, medications: e.target.value})} data-testid="health-medications-input" />
          </label>
          <div className="flex gap-2">
            <button type="submit" className="btn" data-testid="health-save-btn"><Check size={16}/> حفظ البيانات</button>
            {profile && <button type="button" className="btn-ghost btn" onClick={() => setShowForm(false)}>إلغاء</button>}
          </div>
        </form>
      ) : profile && (
        <section className="glass rounded-3xl p-5" data-testid="health-summary-card">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <HeartPulse size={20} style={{ color: "var(--primary)" }} />
              <h3 className="font-bold text-lg">ملفك الصحي</h3>
            </div>
            <button className="btn-ghost btn text-sm" onClick={editProfile} data-testid="health-edit-btn"><RefreshCw size={14}/> تحديث البيانات</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-2xl p-3 bg-white/70 border border-stone-200/60">
              <div className="text-xs text-stone-500">الطول</div>
              <div className="font-black text-xl mt-1">{profile.height_cm}<span className="text-xs"> سم</span></div>
            </div>
            <div className="rounded-2xl p-3 bg-white/70 border border-stone-200/60">
              <div className="text-xs text-stone-500">الوزن</div>
              <div className="font-black text-xl mt-1">{profile.weight_kg}<span className="text-xs"> كغ</span></div>
            </div>
            <div className="rounded-2xl p-3" style={{ background: `${profile.bmi_category?.color}15`, border: `1px solid ${profile.bmi_category?.color}40` }}>
              <div className="text-xs" style={{ color: profile.bmi_category?.color }}>BMI</div>
              <div className="font-black text-xl mt-1" style={{ color: profile.bmi_category?.color }}>{profile.bmi}</div>
              <div className="text-[11px] font-bold" style={{ color: profile.bmi_category?.color }}>{profile.bmi_category?.label_ar}</div>
            </div>
            <div className="rounded-2xl p-3 bg-white/70 border border-stone-200/60">
              <div className="text-xs text-stone-500">الهدف</div>
              <div className="font-bold text-sm mt-1">{ {lose:"إنقاص الوزن", gain:"زيادة الوزن", maintain:"محافظة", fitness:"لياقة"}[profile.goal] || profile.goal }</div>
            </div>
          </div>

          {!analysis && (
            <div className="mt-4 text-center">
              <button className="btn" onClick={runAnalysis} disabled={analyzing} data-testid="health-analyze-btn">
                {analyzing ? <><div className="dots inline-flex"><span/><span/><span/></div> جارٍ التحليل…</> : <><Sparkles size={16}/> احصل على خطتك الشخصية</>}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Analysis result */}
      {analysis && (
        <>
          <section className="glass rounded-3xl p-5" data-testid="health-analysis-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={18} style={{ color: "var(--primary)" }} />
                <h3 className="font-bold text-lg">خطتك الشخصية</h3>
              </div>
              <button className="btn-ghost btn text-sm" onClick={runAnalysis} disabled={analyzing} data-testid="health-reanalyze-btn">
                <RefreshCw size={14}/> {analyzing ? "جارٍ التحديث…" : "إعادة التحليل"}
              </button>
            </div>
            {analysis.summary && <p className="text-stone-700 leading-relaxed mb-3">{analysis.summary}</p>}
            {analysis.daily_calories && (
              <div className="rounded-2xl p-3 bg-amber-50/80 border border-amber-200 mb-3">
                <div className="text-xs text-amber-700 font-bold">🔥 السعرات اليومية المقترحة</div>
                <div className="text-sm text-stone-700 mt-1">{analysis.daily_calories}</div>
              </div>
            )}
            {(analysis.warnings || []).length > 0 && (
              <div className="rounded-2xl p-3 bg-red-50/80 border border-red-200">
                <div className="text-xs text-red-700 font-bold mb-1">⚠️ تنبيهات</div>
                <ul className="text-sm text-stone-700 space-y-1 list-disc ps-5">
                  {analysis.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </section>

          {/* Exercises */}
          {(analysis.exercises || []).length > 0 && (
            <section className="glass rounded-3xl p-5" data-testid="health-exercises-card">
              <div className="flex items-center gap-2 mb-3">
                <Dumbbell size={18} style={{ color: "#059669" }} />
                <h3 className="font-bold text-lg">تمارينك الموصى بها</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analysis.exercises.map((ex, i) => (
                  <div key={i} className="rounded-2xl p-3 bg-white/70 border border-stone-200/60">
                    <div className="font-bold text-stone-800">{ex.name}</div>
                    <div className="text-xs text-stone-500 mt-1">⏱️ {ex.duration_or_reps} · 📅 {ex.frequency}</div>
                    {ex.why && <div className="text-sm text-stone-600 mt-2 leading-relaxed">{ex.why}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Foods */}
          <section className="grid md:grid-cols-2 gap-5">
            {(analysis.foods_recommended || []).length > 0 && (
              <div className="glass rounded-3xl p-5" data-testid="health-foods-rec-card">
                <div className="flex items-center gap-2 mb-3">
                  <Salad size={18} style={{ color: "#16a34a" }} />
                  <h3 className="font-bold text-lg">أطعمة مفيدة لك</h3>
                </div>
                <ul className="space-y-2">
                  {analysis.foods_recommended.map((f, i) => (
                    <li key={i} className="rounded-xl p-2.5 bg-green-50/60 border border-green-200/50">
                      <div className="font-bold text-stone-800 text-sm">✓ {f.name}</div>
                      {f.why && <div className="text-xs text-stone-600 mt-1">{f.why}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(analysis.foods_to_avoid || []).length > 0 && (
              <div className="glass rounded-3xl p-5" data-testid="health-foods-avoid-card">
                <div className="flex items-center gap-2 mb-3">
                  <X size={18} style={{ color: "#dc2626" }} />
                  <h3 className="font-bold text-lg">أطعمة يُفضّل تجنبها</h3>
                </div>
                <ul className="space-y-2">
                  {analysis.foods_to_avoid.map((f, i) => (
                    <li key={i} className="rounded-xl p-2.5 bg-red-50/60 border border-red-200/50">
                      <div className="font-bold text-stone-800 text-sm">✗ {f.name}</div>
                      {f.why && <div className="text-xs text-stone-600 mt-1">{f.why}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Weekly plan */}
          {(analysis.weekly_plan || []).length > 0 && (
            <section className="glass rounded-3xl p-5" data-testid="health-weekly-plan">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={18} style={{ color: "var(--primary)" }} />
                <h3 className="font-bold text-lg">خطتك الأسبوعية</h3>
              </div>
              <div className="space-y-2">
                {analysis.weekly_plan.map((d, i) => (
                  <details key={i} className="rounded-2xl bg-white/70 border border-stone-200/60 group">
                    <summary className="p-3 cursor-pointer font-bold text-stone-800 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-800 text-xs font-black flex items-center justify-center">{i+1}</span>
                      {d.day}
                    </summary>
                    <div className="px-3 pb-3 text-sm text-stone-700 space-y-2">
                      {d.workout && <div><b>💪 التمارين:</b> {d.workout}</div>}
                      {d.meals && <div><b>🍽️ الأكل:</b> {d.meals}</div>}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Lifestyle tips */}
          {(analysis.lifestyle_tips || []).length > 0 && (
            <section className="glass rounded-3xl p-5" data-testid="health-tips">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={18} style={{ color: "#d97706" }} />
                <h3 className="font-bold text-lg">نصائح لنمط حياتك</h3>
              </div>
              <ul className="space-y-2">
                {analysis.lifestyle_tips.map((tip, i) => (
                  <li key={i} className="rounded-xl p-3 bg-amber-50/60 border border-amber-200/40 text-sm text-stone-700">💡 {tip}</li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {/* Weight tracking */}
      {profile && (
        <section className="glass rounded-3xl p-5" data-testid="health-weight-tracker">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={18} style={{ color: "var(--primary)" }} />
            <h3 className="font-bold text-lg">متابعة الوزن</h3>
            <span className="text-xs text-stone-500">(آخر 90 يوم)</span>
          </div>

          <div className="flex flex-wrap gap-2 items-end mb-4">
            <label className="flex-1 min-w-[120px]">
              <span className="text-xs text-stone-600">الوزن الجديد (كغ)</span>
              <input type="number" min="20" max="400" step="0.1" className="input w-full mt-1" value={newWeight} onChange={e => setNewWeight(e.target.value)} placeholder="مثال: 70.5" data-testid="health-newweight-input" />
            </label>
            <label className="flex-1 min-w-[180px]">
              <span className="text-xs text-stone-600">ملاحظة (اختياري)</span>
              <input type="text" className="input w-full mt-1" value={weightNote} onChange={e => setWeightNote(e.target.value)} placeholder="بعد الإفطار / مساء..." data-testid="health-weightnote-input" />
            </label>
            <button className="btn" onClick={addCheckin} data-testid="health-checkin-btn"><Plus size={16}/> سجّل القياس</button>
          </div>

          {checkins.length >= 2 ? <WeightChart data={checkins} /> : (
            <div className="text-sm text-stone-500 text-center p-4">سجّل قياسين على الأقل لرؤية الرسم البياني للتقدم.</div>
          )}

          {checkins.length > 0 && (
            <div className="mt-4 space-y-1.5 max-h-[180px] overflow-y-auto">
              {[...checkins].reverse().slice(0, 10).map(c => (
                <div key={c.id} className="flex items-center justify-between text-xs bg-white/60 rounded-lg p-2 border border-stone-200/60">
                  <span><b>{c.weight_kg}</b> كغ {c.note && <span className="text-stone-500">· {c.note}</span>}</span>
                  <span className="text-stone-500">{new Date(c.created_at).toLocaleDateString("ar-EG", { day: "numeric", month: "short" })}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ---------- Weight Chart (SVG line) ----------
function WeightChart({ data }) {
  if (!data || data.length < 2) return null;
  const w = 720, h = 180, pad = { t: 16, r: 12, b: 28, l: 36 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const weights = data.map(d => d.weight_kg);
  const min = Math.min(...weights) - 1;
  const max = Math.max(...weights) + 1;
  const range = max - min || 1;
  const stepX = innerW / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = pad.l + i * stepX;
    const y = pad.t + innerH - ((d.weight_kg - min) / range) * innerH;
    return [x, y, d];
  });
  const pathD = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  const areaD = `${pathD} L ${pad.l + (data.length - 1) * stepX} ${pad.t + innerH} L ${pad.l} ${pad.t + innerH} Z`;
  const first = data[0].weight_kg;
  const last = data[data.length - 1].weight_kg;
  const diff = +(last - first).toFixed(1);
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("ar-EG", { day: "numeric", month: "short" }); } catch { return iso; } };
  return (
    <div>
      <div className="flex items-center gap-3 mb-2 text-xs">
        <span className="px-2 py-1 rounded-full bg-stone-100 text-stone-700">من <b>{first}</b> إلى <b>{last}</b> كغ</span>
        <span className={`px-2 py-1 rounded-full ${diff < 0 ? "bg-emerald-50 text-emerald-700" : diff > 0 ? "bg-rose-50 text-rose-700" : "bg-stone-100 text-stone-700"}`}>{diff > 0 ? "+" : ""}{diff} كغ</span>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[460px]" style={{ height: 180 }} role="img" aria-label="رسم بياني للوزن">
          <defs>
            <linearGradient id="weightArea" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.5" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          {[0, 0.5, 1].map((p, i) => {
            const y = pad.t + innerH * p;
            const val = (max - range * p).toFixed(1);
            return (<g key={i}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#e7e5e4" strokeDasharray="3 3"/>
              <text x={pad.l - 4} y={y + 4} fontSize="10" textAnchor="end" fill="#78716c">{val}</text>
            </g>);
          })}
          <path d={areaD} fill="url(#weightArea)" />
          <path d={pathD} fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {points.map(([x, y, d], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="4" fill="#fff" stroke="var(--primary)" strokeWidth="2"><title>{`${fmt(d.created_at)}: ${d.weight_kg} كغ`}</title></circle>
            </g>
          ))}
          {points.map(([x], i) => {
            if (i % Math.max(1, Math.ceil(data.length / 6)) !== 0 && i !== data.length - 1) return null;
            return <text key={`x${i}`} x={x} y={h - 8} fontSize="10" textAnchor="middle" fill="#78716c">{fmt(data[i].created_at)}</text>;
          })}
        </svg>
      </div>
    </div>
  );
}

// ---------- Admin Growth Chart ----------
function GrowthChart({ data }) {
  if (!data || data.length === 0) return null;
  const w = 720, h = 220, pad = { t: 16, r: 12, b: 28, l: 28 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const max = Math.max(1, ...data.map(d => d.count));
  const barW = innerW / data.length;
  const total = data.reduce((s, d) => s + d.count, 0);
  const peak = data.reduce((a, b) => (b.count > a.count ? b : a), data[0]);
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString("ar-EG", { day: "numeric", month: "short" }); }
    catch { return iso; }
  };
  // gridlines (4)
  const gridVals = [0, 0.25, 0.5, 0.75, 1].map(p => Math.round(max * p));
  return (
    <section className="glass rounded-3xl p-5" data-testid="admin-growth-chart">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <LineChart size={18} style={{ color: "var(--primary)" }} />
          <h3 className="font-bold text-lg">نمو المشتركين</h3>
          <span className="text-xs text-stone-500">(آخر 30 يوم)</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-800">المجموع: <b>{total}</b></span>
          <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-800">أفضل يوم: <b>{peak.count}</b> ({fmt(peak.date)})</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[520px]" style={{ height: 220 }} role="img" aria-label="رسم بياني للنمو">
          <defs>
            <linearGradient id="growthFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.7" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.15" />
            </linearGradient>
          </defs>
          {gridVals.map((v, i) => {
            const y = pad.t + innerH - (v / max) * innerH;
            return (
              <g key={i}>
                <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#e7e5e4" strokeDasharray="3 3" />
                <text x={pad.l - 4} y={y + 4} fontSize="10" textAnchor="end" fill="#78716c">{v}</text>
              </g>
            );
          })}
          {data.map((d, i) => {
            const barH = (d.count / max) * innerH;
            const x = pad.l + i * barW + barW * 0.18;
            const y = pad.t + innerH - barH;
            const bw = barW * 0.64;
            return (
              <g key={d.date}>
                <rect x={x} y={y} width={bw} height={Math.max(0, barH)} rx="3" fill="url(#growthFill)">
                  <title>{`${fmt(d.date)}: ${d.count} مشترك`}</title>
                </rect>
              </g>
            );
          })}
          {/* x-axis labels every ~5 days */}
          {data.map((d, i) => {
            if (i % 5 !== 0 && i !== data.length - 1) return null;
            const x = pad.l + i * barW + barW / 2;
            return (
              <text key={`x-${d.date}`} x={x} y={h - 8} fontSize="10" textAnchor="middle" fill="#78716c">
                {fmt(d.date)}
              </text>
            );
          })}
        </svg>
      </div>
    </section>
  );
}

// ---------- Admin Dashboard ----------
function AdminPage() {
  const { user, authHeaders, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    setBusy(true);
    axios.get(`${API}/admin/stats`, { headers: authHeaders })
      .then(({ data }) => { setStats(data); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "خطأ"))
      .finally(() => setBusy(false));
  }, [user]);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("ar-EG", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };
  const langName = (c) => ({ ar:"العربية", en:"English", fr:"Français", es:"Español", de:"Deutsch", tr:"Türkçe", ur:"اردو", fa:"فارسی", hi:"हिन्दी", ru:"Русский", zh:"中文", ja:"日本語", ko:"한국어", it:"Italiano", pt:"Português", nl:"Nederlands" }[c] || c);
  const providerName = (p) => ({ email:"بريد إلكتروني", google:"جوجل", facebook:"فيسبوك" }[p] || p);
  const genderName = (g) => ({ male:"ذكر", female:"أنثى", other:"آخر" }[g] || g);

  return (
    <div className="grain min-h-screen" data-testid="admin-page">
      <ThemeManager age={30} mood="motivated" />
      <div className="mood-bg" />
      <header className="sticky top-0 z-30 backdrop-blur-lg bg-white/70 border-b border-stone-200/60">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={18} style={{ color: "var(--primary)" }} />
            <div className="font-black text-lg">لوحة الإدارة</div>
            <span className="text-xs text-stone-500">حياتك</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/app" className="btn-secondary text-sm" data-testid="admin-back-btn">العودة للتطبيق</Link>
            <button className="btn-ghost btn text-sm" onClick={() => { logout(); nav("/login"); }} data-testid="admin-logout-btn">خروج</button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-5">
        {busy && (
          <div className="glass rounded-3xl p-8 text-center">
            <div className="dots mx-auto mb-3"><span/><span/><span/></div>
            <div className="text-stone-600">جارٍ تحميل الإحصائيات…</div>
          </div>
        )}

        {err && !busy && (
          <div className="glass rounded-3xl p-6 text-center" data-testid="admin-error">
            <div className="text-red-600 font-bold">{err}</div>
            <div className="text-xs text-stone-500 mt-2">تأكد أنك مسجّل دخول بحساب المدير (admin@hayatak.app).</div>
          </div>
        )}

        {stats && !busy && (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-main-stats">
              <StatCard icon={Users} color="#8B5E3C" label="إجمالي المشتركين" value={stats.users.total} />
              <StatCard icon={TrendingUp} color="#16a34a" label="انضموا اليوم" value={stats.users.today} suffix={`هذا الأسبوع: ${stats.users.week} · الشهر: ${stats.users.month}`} />
              <StatCard icon={Activity} color="#0ea5e9" label="نشطون اليوم" value={stats.users.active_today} suffix={`خلال 7 أيام: ${stats.users.active_week}`} />
              <StatCard icon={MessageCircle} color="#d97706" label="رسائل المحادثة" value={stats.content.chat_messages} suffix={`اليوم: ${stats.content.chat_messages_today}`} />
            </section>

            {stats.growth_30d && <GrowthChart data={stats.growth_30d} />}

            <section className="glass rounded-3xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={18} style={{ color: "var(--primary)" }} />
                <h3 className="font-bold text-lg">المحتوى داخل التطبيق</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <MiniStat icon={BookOpen} label="يوميات" value={stats.content.journals} />
                <MiniStat icon={Smile} label="مزاج مسجّل" value={stats.content.moods} />
                <MiniStat icon={Target} label="أهداف" value={stats.content.goals} />
                <MiniStat icon={HeartPulse} label="ملفات صحتي" value={stats.content.health_profiles || 0} />
                <MiniStat icon={Calendar} label="مواعيد" value={stats.content.appointments || 0} />
                <MiniStat icon={Brain} label="ذكريات" value={stats.content.memories} />
                <MiniStat icon={ImageIcon} label="صور AI" value={stats.content.images} />
                <MiniStat icon={FileText} label="ملفات PDF" value={stats.content.pdfs} />
              </div>
            </section>

            <section className="grid lg:grid-cols-3 gap-4">
              <DistroCard icon={Languages} title="اللغات المستخدمة" items={stats.languages.map(l => ({ label: langName(l.code), count: l.count }))} total={stats.users.total} />
              <DistroCard icon={Shield} title="طريقة التسجيل" items={stats.providers.map(p => ({ label: providerName(p.provider), count: p.count }))} total={stats.users.total} />
              <DistroCard icon={Users} title="توزيع الجنس" items={stats.genders.map(g => ({ label: genderName(g.gender), count: g.count }))} total={stats.users.total} />
            </section>

            <AllUsersSection authHeaders={authHeaders} fmtDate={fmtDate} langName={langName} providerName={providerName} />

            <FeedbackSection authHeaders={authHeaders} fmtDate={fmtDate} />
          </>
        )}
      </main>
    </div>
  );
}

// ---------- Admin: All Users (with search + pagination) ----------
function AllUsersSection({ authHeaders, fmtDate, langName, providerName }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const limit = 20;

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, {
        headers: authHeaders, params: { skip, limit, q },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {} finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skip, q]);

  const onSearch = (e) => { setSkip(0); setQ(e.target.value); };
  const pages = Math.max(1, Math.ceil(total / limit));
  const curPage = Math.floor(skip / limit) + 1;

  return (
    <section className="glass rounded-3xl p-5" data-testid="admin-all-users">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Users size={18} style={{ color: "var(--primary)" }} />
          <h3 className="font-bold text-lg">كل المشتركين</h3>
          <span className="text-xs text-stone-500">(إجمالي: {total})</span>
        </div>
        <input
          className="input text-sm" placeholder="🔍 ابحث بالاسم أو البريد..."
          value={q} onChange={onSearch}
          style={{ minWidth: 220 }}
          data-testid="admin-users-search"
        />
      </div>
      {busy ? (
        <div className="text-center text-stone-500 py-4"><div className="dots mx-auto mb-2"><span/><span/><span/></div>جارٍ التحميل…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-stone-500 text-center py-4">لا نتائج.</div>
      ) : (
        <div className="space-y-2">
          {items.map(u => (
            <div key={u.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white/70 border border-stone-200/70" data-testid="admin-user-row">
              {u.picture ? (
                <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 font-bold flex items-center justify-center">
                  {(u.name || u.email || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{u.name || "بدون اسم"}</div>
                <div className="text-xs text-stone-500 truncate">{u.email}</div>
              </div>
              <div className="text-left shrink-0">
                <div className="text-[11px] text-stone-500">{fmtDate(u.created_at)}</div>
                <div className="flex items-center gap-1 mt-1 justify-end flex-wrap">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{providerName(u.auth_provider || "email")}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">{langName(u.language || "ar")}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button className="btn-ghost btn text-sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="admin-users-prev">‹ السابق</button>
          <span className="text-xs text-stone-500">صفحة {curPage} من {pages}</span>
          <button className="btn-ghost btn text-sm" disabled={curPage >= pages} onClick={() => setSkip(skip + limit)} data-testid="admin-users-next">التالي ›</button>
        </div>
      )}
    </section>
  );
}

// ---------- Admin: Feedback ----------
function FeedbackSection({ authHeaders, fmtDate }) {
  const [data, setData] = useState({ items: [], unread_count: 0, average_rating: 0, total: 0 });
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("all"); // all / unread

  const load = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/feedback`, { headers: authHeaders });
      setData(res.data || { items: [] });
    } catch {} finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markRead = async (id) => {
    try { await axios.put(`${API}/admin/feedback/${id}/read`, {}, { headers: authHeaders }); await load(); } catch {}
  };
  const del = async (id) => {
    if (!window.confirm("حذف هذا التقييم؟")) return;
    try { await axios.delete(`${API}/admin/feedback/${id}`, { headers: authHeaders }); await load(); } catch {}
  };

  const filtered = filter === "unread" ? data.items.filter(x => !x.read) : data.items;
  const catLabel = (c) => ({ general: "عام", bug: "مشكلة", feature: "اقتراح", praise: "إعجاب" }[c] || c);
  const catColor = (c) => ({ general: "#78716c", bug: "#dc2626", feature: "#0ea5e9", praise: "#16a34a" }[c] || "#78716c");

  return (
    <section className="glass rounded-3xl p-5" data-testid="admin-feedback">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} style={{ color: "var(--primary)" }} />
          <h3 className="font-bold text-lg">تقييمات وملاحظات المستخدمين</h3>
          {data.unread_count > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500 text-white font-bold">{data.unread_count} جديد</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 font-bold">
            <Star size={12} fill="currentColor" /> {data.average_rating || 0} / 5
          </div>
          <span className="text-xs text-stone-500">({data.total} تقييم)</span>
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        {["all","unread"].map(k => (
          <button key={k} className="tab" data-active={filter === k} onClick={() => setFilter(k)} data-testid={`feedback-filter-${k}`}>
            {k === "all" ? "الكل" : "غير مقروء"}
          </button>
        ))}
      </div>
      {busy ? (
        <div className="text-center text-stone-500 py-4">جارٍ التحميل…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-stone-500 text-center py-6">
          <MessageSquare size={32} className="mx-auto mb-2 opacity-50"/>
          {filter === "unread" ? "لا توجد تقييمات جديدة." : "لم يصلك أي تقييم بعد."}
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {filtered.map(fb => (
            <div key={fb.id} className="p-3 rounded-2xl bg-white/70 border border-stone-200/70" style={{ borderRightWidth: fb.read ? 1 : 3, borderRightColor: fb.read ? "#e7e5e4" : catColor(fb.category) }} data-testid="feedback-row">
              <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={14} fill={i <= fb.rating ? "#f59e0b" : "none"} stroke="#f59e0b" />
                    ))}
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full text-white font-bold" style={{ background: catColor(fb.category) }}>{catLabel(fb.category)}</span>
                  {!fb.read && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500 text-white font-bold">جديد</span>}
                </div>
                <div className="text-[11px] text-stone-500">{fmtDate(fb.created_at)}</div>
              </div>
              <div className="text-sm text-stone-800 mb-2 leading-relaxed">{fb.message}</div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-stone-500">
                  <b>{fb.user_name || "مجهول"}</b> · {fb.user_email}
                </div>
                <div className="flex gap-1">
                  {!fb.read && <button className="btn-ghost btn text-xs" onClick={() => markRead(fb.id)} data-testid="feedback-mark-read"><Check size={12}/> قرأت</button>}
                  <button className="btn-ghost btn text-xs" onClick={() => del(fb.id)} data-testid="feedback-delete"><Trash2 size={12}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StatCard({ icon: Icon, color, label, value, suffix }) {
  return (
    <div className="glass rounded-2xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-stone-500 font-semibold mb-1">{label}</div>
          <div className="text-3xl font-black" style={{ color }}>{(value || 0).toLocaleString("ar-EG")}</div>
          {suffix && <div className="text-[11px] text-stone-500 mt-1">{suffix}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
          <Icon size={20} style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl p-3 bg-white/70 border border-stone-200/70 text-center">
      <Icon size={18} className="mx-auto mb-1" style={{ color: "var(--primary)" }} />
      <div className="text-xl font-black">{(value || 0).toLocaleString("ar-EG")}</div>
      <div className="text-[11px] text-stone-500">{label}</div>
    </div>
  );
}

function DistroCard({ icon: Icon, title, items, total }) {
  return (
    <div className="glass rounded-3xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} style={{ color: "var(--primary)" }} />
        <h3 className="font-bold">{title}</h3>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-stone-500">لا بيانات</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => {
            const pct = total > 0 ? Math.round((it.count / total) * 100) : 0;
            return (
              <div key={i}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold text-stone-700">{it.label}</span>
                  <span className="text-stone-500">{it.count} ({pct}%)</span>
                </div>
                <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--primary)" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ---------- App ----------
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/login" element={<AnonOnly><Login /></AnonOnly>} />
          <Route path="/register" element={<AnonOnly><Register /></AnonOnly>} />
          <Route path="/forgot-password" element={<AnonOnly><ForgotPassword /></AnonOnly>} />
          <Route path="/reset-password" element={<AnonOnly><ResetPassword /></AnonOnly>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
